import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  X, Play, Pause, SkipBack, SkipForward, Scissors, Square,
  Type, ArrowRight, Download, Monitor, Undo2, Redo2, Maximize2, Minimize2,
  ZoomIn, ZoomOut, Layers, ChevronRight, Trash2, Move, UploadCloud, BookmarkPlus,
  Circle, Minus, ImagePlus, Smile, Mic, Check, RotateCcw, VolumeX, Volume2, Plus, SplitSquareHorizontal, EyeOff,
  Keyboard, Save,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Video } from '../types/video';
import type { VideoEditDraft, VideoEditDraftProject } from '../types/videoEditDraft';
import { VIDEO_EDIT_DRAFT_SCHEMA_VERSION } from '../types/videoEditDraft';
import { upsertVideoEditDraft, getVideoEditDraft } from '../lib/videoEditDraftStorage';
import { useVideos, broadcastVideosRefresh } from '../hooks/useVideos';
import { useAuth } from '../hooks/useAuth';
import { useStorage, StorageInfo } from '../hooks/useStorage';
import StorageExceededDialog from '../components/StorageExceededDialog';
import { VideoEditorPropertiesPanel, type VideoEditorPropertiesSelection } from '../components/VideoEditorPropertiesPanel';
import {
  drawTextAnnotation,
  getTextAnnotationBounds,
} from '../utils/videoEditorTextCanvas';
import './VideoEditorPage.css';
import { VideoFramePanel } from '../components/VideoFramePanel';
import { DEFAULT_FRAME, containRect, drawFramedVideo, normalizeFrame, type Source as FrameSource, type VideoFrameStyle } from '../lib/videoFrame';
import { VideoZoomPanel } from '../components/VideoZoomPanel';
import {
  DEFAULT_ZOOM_LENGTH, DEFAULT_ZOOM_SCALE, MIN_ZOOM_LENGTH, normalizeZooms, zoomAt, zoomLimits, type ZoomSegment,
} from '../lib/videoZoom';
import { toast } from '../lib/toast';
import {
  NO_CUTS, addSplit, exportTimeAt, keptRanges, normalizeCuts, partAt, removePart, removedAt, restorePart, totalLength,
  type TimeRange, type VideoCuts,
} from '../lib/videoCuts';
import { apiClient } from '../lib/apiClient';
import { BLUR_STRENGTH, blurBoxOnScreen, drawBlur, zoomMapping, type Box, type BlurStyle } from '../lib/videoBlur';
import { richTextToPlain } from '../lib/richText';
import { saveAsMp4ToastId } from '../hooks/useMp4Download';

// ---------- Types ----------

type AnnotationType = 'text' | 'rect' | 'arrow' | 'circle' | 'line' | 'image' | 'emoji' | 'audio' | 'blur';
type DrawToolType = 'rect' | 'arrow' | 'circle' | 'line' | 'blur';
type ToolType = 'none' | 'move' | AnnotationType;
type EditorMode = 'basic' | 'advanced';

// ---------- Geometry helpers ----------

/** Squared distance from point P to line segment AB. */
function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Returns the annotation hit by a canvas-pixel click, or null.
 *  Only annotations shown at `currentTime` count, plus `alsoId` (the selected one, which the
 *  Move tool shows outside its time) — matches canvas behaviour. */
function hitTestAnnotation(
  anns: Annotation[],
  cx: number, cy: number,
  cw: number, ch: number,
  currentTime: number,
  alsoId: string | null = null,
  /** Where a blur box shows (it follows the zoom), in canvas pixels. */
  blurBox: ((ann: Annotation) => Box) | null = null,
): Annotation | null {
  for (let i = anns.length - 1; i >= 0; i--) {
    const ann = anns[i];
    if (ann.id !== alsoId && (currentTime < ann.inTime || currentTime > ann.outTime)) continue;
    const ax = ann.x * cw, ay = ann.y * ch;
    const aw = ann.w * cw, ah = ann.h * ch;
    if (ann.type === 'text') {
      const measureCanvas = document.createElement('canvas');
      measureCanvas.width = Math.max(1, cw);
      measureCanvas.height = Math.max(1, ch);
      const mctx = measureCanvas.getContext('2d');
      if (!mctx) continue;
      const b = getTextAnnotationBounds(ann, cw, ch, mctx);
      if (cx >= b.x0 && cx <= b.x1 && cy >= b.y0 && cy <= b.y1) return ann;
    } else if (ann.type === 'blur' && blurBox) {
      const b = blurBox(ann);
      if (cx >= b.x && cx <= b.x + b.w && cy >= b.y && cy <= b.y + b.h) return ann;
    } else if (ann.type === 'rect' || ann.type === 'image' || ann.type === 'blur') {
      const x0 = Math.min(ax, ax + aw), x1 = Math.max(ax, ax + aw);
      const y0 = Math.min(ay, ay + ah), y1 = Math.max(ay, ay + ah);
      if (cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1) return ann;
    } else if (ann.type === 'circle') {
      const x0 = Math.min(ax, ax + aw), x1 = Math.max(ax, ax + aw);
      const y0 = Math.min(ay, ay + ah), y1 = Math.max(ay, ay + ah);
      const rcx = (x0 + x1) / 2, rcy = (y0 + y1) / 2;
      const rx = Math.max((x1 - x0) / 2, 1);
      const ry = Math.max((y1 - y0) / 2, 1);
      const nrx = (cx - rcx) / rx, nry = (cy - rcy) / ry;
      if (nrx * nrx + nry * nry <= 1.08) return ann;
    } else if (ann.type === 'arrow' || ann.type === 'line') {
      if (distToSegment(cx, cy, ax, ay, ax + aw, ay + ah) <= 12) return ann;
    } else if (ann.type === 'emoji') {
      const mc = document.createElement('canvas');
      mc.width = mc.height = 4;
      const mx = mc.getContext('2d');
      if (!mx) continue;
      const fs = Math.max(8, ann.fontSize || 48);
      mx.font = `${fs}px sans-serif`;
      const tw = mx.measureText(ann.text || '🙂').width;
      const tx = ax;
      const ty = ay;
      if (cx >= tx && cx <= tx + tw && cy >= ty && cy <= ty + fs * 1.05) return ann;
    } else if (ann.type === 'audio') {
      const x0 = Math.min(ax, ax + aw), x1 = Math.max(ax, ax + aw);
      const y0 = Math.min(ay, ay + ah), y1 = Math.max(ay, ay + ah);
      if (cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1) return ann;
    }
  }
  return null;
}

interface Annotation {
  id: string;
  type: AnnotationType;
  x: number;    // 0..1 ratio of canvas width
  y: number;    // 0..1 ratio of canvas height
  w: number;    // 0..1 ratio — width for rect/arrow endpoint
  h: number;    // 0..1 ratio — height for rect/arrow endpoint
  text: string;
  color: string;
  fontSize: number;
  inTime: number;
  outTime: number;
  /** Text-only — font stack (matches Whiteboard list where possible) */
  fontFamily?: string;
  /** Text-only — CSS color; `transparent` = no box */
  backgroundColor?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  /** Text-only — max line wrap width as fraction of canvas width */
  textBoxWidthRatio?: number;
  /** Rect / circle — CSS color inside stroke */
  fillColor?: string;
  /** image — data URL or http(s) */
  imageSrc?: string;
  /** audio clip */
  audioSrc?: string;
  audioDurationSec?: number;
  /** Per-clip playback volume 0..1 (separate audio timeline + export) */
  audioVolume?: number;
  /** Blur — pixelated (default) or blurred, block size in px (lib/videoBlur) */
  blurStyle?: BlurStyle;
  blurStrength?: number;
}

interface VideoEditorPageProps {
  video: Video;
  onClose: () => void;
  /** When resuming from My Library → Drafts */
  resumeDraftId?: string | null;
  initialDraftProject?: VideoEditDraftProject | null;
}

// ---------- Helpers ----------

function fmtTime(sec: number): string {
  if (!isFinite(sec) || isNaN(sec)) return '--:--';
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

async function blobUrlToDataUrl(url: string): Promise<string> {
  try {
    if (!url.startsWith('blob:')) return url;
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result as string);
      fr.onerror = () => reject(new Error('read'));
      fr.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

let annCounter = 0;
function newAnnId(): string {
  return `ann-${++annCounter}-${Date.now()}`;
}

function formatVoiceElapsed(sec: number): string {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  const ds = Math.min(9, Math.floor((s % 1) * 10));
  return `${m}:${String(ss).padStart(2, '0')}.${ds}`;
}

/** Widest the editor canvas is drawn (4K): bigger sources are scaled down to this. */
const MAX_CANVAS_WIDTH = 3840;

// ---------- Component ----------

export default function VideoEditorPage({
  video,
  onClose,
  resumeDraftId = null,
  initialDraftProject = null,
}: VideoEditorPageProps) {
  const { t } = useTranslation();
  const { uploadVideo } = useVideos();
  const { user } = useAuth();
  const { getStorageInfo } = useStorage();
  const [storageExceeded, setStorageExceeded] = useState(false);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);

  // Keyboard shortcuts list (header button).
  const [showShortcuts, setShowShortcuts] = useState(false);
  const shortcutsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showShortcuts) return;
    const onDown = (e: MouseEvent) => {
      if (!shortcutsRef.current?.contains(e.target as Node)) setShowShortcuts(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setShowShortcuts(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [showShortcuts]);

  // ---- Mode ----
  const [mode, setMode] = useState<EditorMode>('advanced');
  // Frame around the video; read by the draw loop through the ref (preview and export).
  const [frame, setFrame] = useState<VideoFrameStyle>(DEFAULT_FRAME);
  const frameRef = useRef<VideoFrameStyle>(DEFAULT_FRAME);
  frameRef.current = frame;
  // Zoom segments (timeline "Zoom" row); also read by the draw loop through the ref.
  const [zooms, setZooms] = useState<ZoomSegment[]>([]);
  const zoomsRef = useRef<ZoomSegment[]>([]);
  zoomsRef.current = zooms;
  const [selectedZoom, setSelectedZoom] = useState<string | null>(null);
  const veRootRef = useRef<HTMLDivElement>(null);
  const [editorIsFullscreen, setEditorIsFullscreen] = useState(false);

  useEffect(() => {
    const syncFullscreen = () => {
      setEditorIsFullscreen(document.fullscreenElement === veRootRef.current);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  const toggleEditorFullscreen = useCallback(async () => {
    const root = veRootRef.current;
    if (!root) return;
    try {
      if (document.fullscreenElement === root) {
        await document.exitFullscreen();
      } else {
        await root.requestFullscreen();
      }
    } catch {
      /* Fullscreen may be unavailable (e.g. iframe policy). */
    }
  }, []);

  // ---- Playback ----
  const [currentTime, setCurrentTime] = useState(0);
  // video.duration from DB may be Infinity (WebM recorded via MediaRecorder) or undefined.
  const safeDuration = isFinite(video.duration ?? 0) ? (video.duration ?? 0) : 0;
  const [duration, setDuration] = useState(safeDuration);
  const [isPlaying, setIsPlaying] = useState(false);
  // Native aspect ratio of the source video — used to size the canvas correctly.
  const [videoAspect, setVideoAspect] = useState<number>(16 / 9);

  // ---- Trim ----
  const [inPoint, setInPoint] = useState(0);
  const [outPoint, setOutPoint] = useState(safeDuration);
  const inPointRef = useRef(0);
  const outPointRef = useRef(safeDuration);
  useEffect(() => { inPointRef.current = inPoint; }, [inPoint]);
  useEffect(() => { outPointRef.current = outPoint; }, [outPoint]);

  // ---- Cuts (parts removed from the middle; lib/videoCuts) ----
  const [cuts, setCuts] = useState<VideoCuts>(NO_CUTS);
  const cutsRef = useRef<VideoCuts>(NO_CUTS);
  // Real-time capture only: skips a removed part while recording (set while it runs).
  const captureSkipRef = useRef<((removed: TimeRange) => void) | null>(null);

  // ---- Original audio mute ----
  const [muteOriginalAudio, setMuteOriginalAudio] = useState(false);
  useEffect(() => {
    // After createMediaElementSource is called, v.muted has no effect.
    // Gain is the only way to silence/restore audio through the WebAudio graph.
    if (originalAudioGainRef.current) {
      originalAudioGainRef.current.gain.value = muteOriginalAudio ? 0 : 1;
    }
  }, [muteOriginalAudio]);

  // ---- Annotations ----
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const annotationsRef = useRef<Annotation[]>([]);
  useEffect(() => { annotationsRef.current = annotations; }, [annotations]);

  useEffect(() => {
    const map = annotationImagesRef.current;
    const keep = new Set<string>();
    for (const ann of annotations) {
      if (ann.type !== 'image' || !ann.imageSrc) continue;
      keep.add(ann.id);
      const prev = map.get(ann.id);
      if (prev && prev.dataset.src === ann.imageSrc) continue;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.dataset.src = ann.imageSrc;
      img.onload = () => setImageLoadTick(x => x + 1);
      img.onerror = () => setImageLoadTick(x => x + 1);
      img.src = ann.imageSrc;
      map.set(ann.id, img);
    }
    for (const id of map.keys()) {
      if (!keep.has(id)) map.delete(id);
    }
  }, [annotations]);

  useEffect(() => {
    const m = videoAudioElementsRef.current;
    const keep = new Set<string>();
    for (const ann of annotations) {
      if (ann.type !== 'audio' || !ann.audioSrc) continue;
      keep.add(ann.id);
      let el = m.get(ann.id);
      if (!el) {
        el = document.createElement('audio');
        el.preload = 'auto';
        m.set(ann.id, el);
      }
      if (el.src !== ann.audioSrc) {
        el.pause();
        el.src = ann.audioSrc;
        void el.load();
      }
      el.volume = Math.max(0, Math.min(1, ann.audioVolume ?? 1));
    }
    for (const id of m.keys()) {
      if (!keep.has(id)) {
        m.get(id)?.pause();
        m.delete(id);
      }
    }
  }, [annotations]);

  const [selectedTool, setSelectedTool] = useState<ToolType>('none');
  const [selectedAnn, setSelectedAnn] = useState<string | null>(null);

  // Text input overlay state
  const [pendingText, setPendingText] = useState<{ x: number; y: number } | null>(null);
  const [pendingTextValue, setPendingTextValue] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);
  const [imageLoadTick, setImageLoadTick] = useState(0);
  const [emojiChar, setEmojiChar] = useState('😀');
  const [voiceCapturePhase, setVoiceCapturePhase] = useState<'idle' | 'recording' | 'paused' | 'review'>('idle');
  const [voiceReviewUrl, setVoiceReviewUrl] = useState<string | null>(null);
  const voiceReviewBlobRef = useRef<Blob | null>(null);
  const voiceDiscardOnStopRef = useRef(false);
  const voiceRecordedMsRef = useRef(0);
  const voiceSegmentClockRef = useRef<number | null>(null);
  const [voiceUiTick, setVoiceUiTick] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const pendingImageRatioRef = useRef<{ x: number; y: number } | null>(null);
  const annotationImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const videoAudioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const voiceRecordStartRef = useRef(0);
  const voiceChunksRef = useRef<Blob[]>([]);
  const voiceMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceStreamRef = useRef<MediaStream | null>(null);

  const canPauseMediaRecorder =
    typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.prototype.pause === 'function';

  useEffect(() => {
    if (voiceCapturePhase !== 'recording') return;
    const id = window.setInterval(() => setVoiceUiTick(x => x + 1), 250);
    return () => clearInterval(id);
  }, [voiceCapturePhase]);

  // Canvas hover position — used to draw the crosshair cursor indicator
  const canvasHoverRef = useRef<{ x: number; y: number } | null>(null);
  // Ref mirrors of state values needed inside the RAF draw loop
  const selectedToolRef = useRef<ToolType>('none');
  useEffect(() => { selectedToolRef.current = selectedTool; }, [selectedTool]);

  // Focus the text input synchronously after React commits the DOM.
  // useLayoutEffect fires before the browser paints, so the input is focused
  // before any mouseUp/click event on the canvas can steal focus.
  useLayoutEffect(() => {
    if (pendingText && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [pendingText]);

  // Draw drag state (rect/arrow)
  const [drawingStart, setDrawingStart] = useState<{ x: number; y: number } | null>(null);
  const [drawingCurrent, setDrawingCurrent] = useState<{ x: number; y: number } | null>(null);

  // ---- Timeline annotation resize (in/out handles on each marker) ----
  const [resizingAnn, setResizingAnn] = useState<{
    id: string;
    edge: 'left' | 'right' | 'center';
    startTime: number;    // video time at the initial mousedown click
    origInTime: number;
    origOutTime: number;
    /** Which timeline row is being dragged (same timebase, different DOM track width) */
    track: 'video' | 'audio' | 'zoom';
  } | null>(null);
  const timelineTrackRef = useRef<HTMLDivElement>(null);
  const timelineAudioTrackRef = useRef<HTMLDivElement>(null);
  const timelineZoomTrackRef = useRef<HTMLDivElement>(null);

  // Move tool drag state
  const [draggingAnn, setDraggingAnn] = useState<{
    id: string;
    startX: number;   // ratio where drag began
    startY: number;
    origX: number;    // annotation's original position
    origY: number;
  } | null>(null);
  const draggingAnnRef = useRef<typeof draggingAnn>(null);
  useEffect(() => { draggingAnnRef.current = draggingAnn; }, [draggingAnn]);

  // ---- Undo / Redo (ref-based to avoid stale closures); annotations and cuts ----
  const historyStack = useRef<{ anns: Annotation[]; cuts: VideoCuts }[]>([{ anns: [], cuts: NO_CUTS }]);
  const historyIdx = useRef(0);

  const pushHistory = (anns: Annotation[]) => {
    const h = historyStack.current.slice(0, historyIdx.current + 1);
    h.push({ anns: [...anns], cuts: cutsRef.current });
    historyStack.current = h;
    historyIdx.current = h.length - 1;
  };

  const restoreHistory = (i: number) => {
    historyIdx.current = i;
    const { anns, cuts: c } = historyStack.current[i];
    setAnnotations([...anns]);
    annotationsRef.current = [...anns];
    setCuts(c);
    cutsRef.current = c;
  };

  const undo = useCallback(() => {
    restoreHistory(Math.max(0, historyIdx.current - 1));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const redo = useCallback(() => {
    restoreHistory(Math.min(historyStack.current.length - 1, historyIdx.current + 1));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeCuts = useCallback((next: VideoCuts) => {
    cutsRef.current = next;
    setCuts(next);
    pushHistory([...annotationsRef.current]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyAnnPatch = useCallback((id: string, patch: Partial<Annotation>) => {
    setAnnotations(prev => {
      const next = prev.map(a => (a.id === id ? { ...a, ...patch } : a));
      annotationsRef.current = next;
      return next;
    });
  }, []);

  const commitAnnotationsHistory = useCallback(() => {
    pushHistory([...annotationsRef.current]);
  }, []);

  const selectedTiming = useMemo(() => {
    const a = annotations.find(x => x.id === selectedAnn);
    return a ? { inTime: a.inTime, outTime: a.outTime, duration } : undefined;
  }, [annotations, selectedAnn, duration]);

  const selectedForProperties = useMemo((): VideoEditorPropertiesSelection | null => {
    if (!selectedAnn) return null;
    const a = annotations.find(x => x.id === selectedAnn);
    if (!a) return null;
    if (a.type === 'text') {
      return {
        type: 'text',
        id: a.id,
        x: a.x,
        y: a.y,
        text: a.text,
        color: a.color,
        fontSize: a.fontSize,
        fontFamily: a.fontFamily,
        backgroundColor: a.backgroundColor,
        bold: a.bold,
        italic: a.italic,
        underline: a.underline,
        textAlign: a.textAlign,
        textBoxWidthRatio: a.textBoxWidthRatio,
      };
    }
    if (a.type === 'rect') {
      return {
        type: 'rect',
        id: a.id,
        x: a.x,
        y: a.y,
        w: a.w,
        h: a.h,
        color: a.color,
        fillColor: a.fillColor,
      };
    }
    if (a.type === 'circle') {
      return {
        type: 'circle',
        id: a.id,
        x: a.x,
        y: a.y,
        w: a.w,
        h: a.h,
        color: a.color,
        fillColor: a.fillColor,
      };
    }
    if (a.type === 'blur') {
      return {
        type: 'blur',
        id: a.id,
        x: a.x,
        y: a.y,
        w: a.w,
        h: a.h,
        blurStyle: a.blurStyle ?? 'pixelate',
        blurStrength: a.blurStrength ?? BLUR_STRENGTH.default,
      };
    }
    if (a.type === 'arrow') {
      return {
        type: 'arrow',
        id: a.id,
        x: a.x,
        y: a.y,
        w: a.w,
        h: a.h,
        color: a.color,
      };
    }
    if (a.type === 'line') {
      return {
        type: 'line',
        id: a.id,
        x: a.x,
        y: a.y,
        w: a.w,
        h: a.h,
        color: a.color,
      };
    }
    if (a.type === 'image') {
      return {
        type: 'image',
        id: a.id,
        x: a.x,
        y: a.y,
        w: a.w,
        h: a.h,
        imageSrc: a.imageSrc ?? '',
        color: a.color,
      };
    }
    if (a.type === 'emoji') {
      return {
        type: 'emoji',
        id: a.id,
        x: a.x,
        y: a.y,
        text: a.text,
        color: a.color,
        fontSize: a.fontSize,
      };
    }
    return {
      type: 'audio',
      id: a.id,
      x: a.x,
      y: a.y,
      w: a.w,
      h: a.h,
      text: a.text,
      color: a.color,
      fontSize: a.fontSize,
      audioSrc: a.audioSrc ?? '',
      audioDurationSec: a.audioDurationSec,
      audioVolume: a.audioVolume ?? 1,
    };
  }, [annotations, selectedAnn]);

  const annotationTimelineLabel = useCallback((ann: Annotation) => {
    if (ann.type === 'text' && ann.text) return ann.text.slice(0, 24);
    if (ann.type === 'emoji' && ann.text) return ann.text;
    if (ann.type === 'image') return t('video_editor_tools.image');
    if (ann.type === 'blur') return t('video_editor_blur.label', 'Blur');
    if (ann.type === 'audio') return ann.text || t('video_editor_tools.voice_clip_label');
    return ann.type;
  }, [t]);

  // ---- Timeline zoom (advanced) ----
  const [timelineZoom, setTimelineZoom] = useState(1);

  // ---- Save As ----
  const [showSaveModal, setShowSaveModal] = useState(false);
  // A "(copy 1)" suffix so the saved video is told apart from the original.
  const copyName = useCallback((title: string) => t('video_editor_save.copy_name', '{{title}} (copy {{n}})', { title, n: 1, interpolation: { escapeValue: false } }), [t]);
  const [saveFileName, setSaveFileName] = useState(() => copyName(video.title || 'edited-video'));
  // Follow each Save as MP4 while the editor stays open, and say when it ends.
  const mp4FollowTimers = useRef(new Map<string, number>());
  useEffect(() => () => mp4FollowTimers.current.forEach((id) => window.clearTimeout(id)), []);
  const followSaveAsMp4 = useCallback((videoId: string) => {
    const timers = mp4FollowTimers.current;
    const startedAt = Date.now();
    const poll = async () => {
      timers.delete(videoId);
      const { data, error } = await apiClient.getVideoJob(videoId)
        .catch((e: Error) => ({ data: null, error: e }));
      // Deleted meanwhile (or no longer the owner's): nothing to report.
      const status = (error as { status?: number } | null)?.status ?? 0;
      if (status >= 400 && status < 500) return;
      const job = data?.job;
      if (job?.status === 'done') {
        toast.success(t('mp4.saved', 'Saved as MP4.'), { id: saveAsMp4ToastId(videoId) });
        broadcastVideosRefresh();
        return;
      }
      if (job?.status === 'failed') {
        toast.error(t('mp4.save_failed', 'Could not save the video as MP4. It stays as WebM.'), { id: saveAsMp4ToastId(videoId) });
        return;
      }
      // Gives up after an hour (the job's own limit); the library still shows the result.
      if (Date.now() - startedAt < 60 * 60_000) timers.set(videoId, window.setTimeout(poll, 2000));
    };
    window.clearTimeout(timers.get(videoId));
    timers.set(videoId, window.setTimeout(poll, 1000));
  }, [t]);
  const saveFileNameRef = useRef(saveFileName);
  useEffect(() => { saveFileNameRef.current = saveFileName; }, [saveFileName]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureProgress, setCaptureProgress] = useState(0);
  const [captureKind, setCaptureKind] = useState<'download' | 'library' | null>(null);
  // After rendering, the upload to My Library (shown as a loader in the save dialog).
  const [isUploading, setIsUploading] = useState(false);
  const captureCancelledRef = useRef(false);
  const fastExportAbortRef = useRef<AbortController | null>(null);
  // Leaving the editor stops a running export (nothing is saved).
  useEffect(() => () => fastExportAbortRef.current?.abort(), []);
  // Fast export runs (no playback): the "plays through once in real time" note doesn't apply.
  const [fastRender, setFastRender] = useState(false);
  const overlaySourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const pendingSaveKindRef = useRef<'download' | 'library' | null>(null);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(() => resumeDraftId ?? null);
  useEffect(() => { setActiveDraftId(resumeDraftId ?? null); }, [resumeDraftId]);
  const draftHydratedRef = useRef(false);
  useEffect(() => { draftHydratedRef.current = false; }, [video.id, resumeDraftId]);

  // ---- Refs ----
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const isCapturingRef = useRef(false);
  const captureEndRef = useRef(0);
  const chunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const audioSrcRef = useRef<MediaElementAudioSourceNode | null>(null);
  const originalAudioGainRef = useRef<GainNode | null>(null);
  const scrubberRef = useRef<HTMLDivElement>(null);
  /** Last decoded video frame — avoids dark flash while `readyState` dips during rapid seeks */
  const videoFrameCacheRef = useRef<HTMLCanvasElement | null>(null);
  const draggingHandle = useRef<'in' | 'out' | 'playhead' | null>(null);
  // Flag: we seeked to 1e10 to force the browser to scan the WebM blob and report real duration.
  const fixingInfinityDurationRef = useRef(false);

  // ---------- Canvas draw loop ----------

  /**
   * One picture of the edit at time `t`, in display units (W × H; the caller sets the scale):
   * video with frame style and zoom, annotations, and — while `editing` — editing aids. Used by the
   * live preview and by the export, so both look the same.
   */
  const drawScene = useCallback((
    ctx: CanvasRenderingContext2D,
    W: number,
    H: number,
    source: FrameSource | null,
    t: number,
    editing: boolean,
  ) => {
    // Video (frame style, zoom) or a dark placeholder while no frame is available.
    const zoom = zoomAt(zoomsRef.current, t);
    if (source) {
      drawFramedVideo(ctx, source, W, H, frameRef.current, zoom);
    } else {
      // Canvas colours can't be CSS variables.
      ctx.fillStyle = '#111111';
      ctx.fillRect(0, 0, W, H);
    }

    // Annotations at the current time. With the Move tool, the selected one is also shown (dotted)
    // outside its time so it can still be moved. Editing aids are never drawn into an export.
    const showAllForMove = editing && selectedToolRef.current === 'move';
    const blurMap = zoomMapping(W, H, frameRef.current, zoom);

    for (const ann of annotationsRef.current) {
      const timeVisible = t >= ann.inTime && t <= ann.outTime;
      const ghost = showAllForMove && !timeVisible && ann.id === selectedAnn;
      if (!timeVisible && !ghost) continue;
      const ax = ann.x * W;
      const ay = ann.y * H;
      const aw = ann.w * W;
      const ah = ann.h * H;

      if (ann.type === 'blur') {
        // Hides what is drawn below; while editing its area is outlined (never in the export).
        const b = blurBoxOnScreen(ann, W, H, blurMap);
        if (!ghost) drawBlur(ctx, b, ann.blurStyle ?? 'pixelate', ann.blurStrength ?? BLUR_STRENGTH.default);
        if (editing) {
          const sel = showAllForMove && selectedAnn === ann.id;
          ctx.save();
          ctx.strokeStyle = sel ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.6)';
          ctx.lineWidth = sel ? 2 : 1;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(b.x, b.y, b.w, b.h);
          ctx.restore();
        }
        continue;
      }

      if (ann.type === 'text') {
        drawTextAnnotation(ctx, ann, W, H, ghost);
        if (showAllForMove && selectedAnn === ann.id) {
          const b = getTextAnnotationBounds(ann, W, H, ctx);
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.95)';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(b.x0, b.y0, b.x1 - b.x0, b.y1 - b.y0);
          ctx.restore();
        }
        continue;
      }

      if (ann.type === 'emoji') {
        ctx.save();
        if (ghost) ctx.globalAlpha = 0.42;
        const fs = Math.max(8, ann.fontSize || 48);
        ctx.font = `${fs}px sans-serif`;
        ctx.textBaseline = 'top';
        ctx.fillStyle = ann.color;
        ctx.fillText(ann.text || '🙂', ax, ay);
        ctx.restore();
        if (showAllForMove && selectedAnn === ann.id) {
          ctx.save();
          ctx.font = `${fs}px sans-serif`;
          const tw = ctx.measureText(ann.text || '🙂').width;
          ctx.strokeStyle = 'rgba(255,255,255,0.95)';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(ax - 2, ay - 2, tw + 4, fs + 4);
          ctx.restore();
        }
        continue;
      }

      if (ann.type === 'image' && ann.imageSrc) {
        const img = annotationImagesRef.current.get(ann.id);
        ctx.save();
        if (ghost) ctx.globalAlpha = 0.42;
        if (img?.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, ax, ay, aw, ah);
        } else {
          ctx.strokeStyle = ann.color;
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 4]);
          ctx.strokeRect(ax, ay, aw, ah);
        }
        ctx.restore();
        if (showAllForMove && selectedAnn === ann.id) {
          ctx.save();
          const sx = Math.min(ax, ax + aw) - 4;
          const sy = Math.min(ay, ay + ah) - 4;
          const sw = Math.abs(aw) + 8;
          const sh = Math.abs(ah) + 8;
          ctx.strokeStyle = 'rgba(255,255,255,0.95)';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(sx, sy, sw, sh);
          ctx.restore();
        }
        continue;
      }

      if (ann.type === 'audio') {
        const x0 = Math.min(ax, ax + aw);
        const y0 = Math.min(ay, ay + ah);
        const bw = Math.abs(aw);
        const bh = Math.abs(ah);
        ctx.save();
        if (ghost) ctx.globalAlpha = 0.42;
        ctx.fillStyle = 'rgba(15, 23, 42, 0.72)';
        ctx.fillRect(x0, y0, bw, bh);
        ctx.strokeStyle = ann.color;
        ctx.lineWidth = 2;
        ctx.strokeRect(x0, y0, bw, bh);
        ctx.fillStyle = '#f8fafc';
        ctx.font = `${Math.max(10, ann.fontSize || 12)}px sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        const label = `♪ ${(ann.text || 'Audio').slice(0, 18)}`;
        ctx.fillText(label, x0 + 8, y0 + bh / 2);
        ctx.restore();
        if (showAllForMove && selectedAnn === ann.id) {
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.95)';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(x0 - 4, y0 - 4, bw + 8, bh + 8);
          ctx.restore();
        }
        continue;
      }

      ctx.save();
      if (ghost) {
        ctx.globalAlpha = 0.42;
        ctx.setLineDash([5, 4]);
      }
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = ann.color;
      ctx.lineWidth = ghost ? 2 : 2.5;

      if (ann.type === 'rect') {
        const fc = ann.fillColor;
        if (fc && fc !== 'transparent') {
          ctx.fillStyle = fc;
          const prevA = ctx.globalAlpha;
          ctx.globalAlpha = ghost ? 0.22 : 0.35;
          ctx.fillRect(ax, ay, aw, ah);
          ctx.globalAlpha = prevA;
          ctx.fillStyle = ann.color;
        }
        ctx.strokeRect(ax, ay, aw, ah);
      } else if (ann.type === 'circle') {
        const x0 = Math.min(ax, ax + aw);
        const x1 = Math.max(ax, ax + aw);
        const y0 = Math.min(ay, ay + ah);
        const y1 = Math.max(ay, ay + ah);
        const rcx = (x0 + x1) / 2;
        const rcy = (y0 + y1) / 2;
        const rx = Math.max((x1 - x0) / 2, 0.5);
        const ry = Math.max((y1 - y0) / 2, 0.5);
        ctx.beginPath();
        ctx.ellipse(rcx, rcy, rx, ry, 0, 0, Math.PI * 2);
        const fc = ann.fillColor;
        if (fc && fc !== 'transparent') {
          ctx.fillStyle = fc;
          const prevA = ctx.globalAlpha;
          ctx.globalAlpha = ghost ? 0.22 : 0.35;
          ctx.fill();
          ctx.globalAlpha = prevA;
          ctx.fillStyle = ann.color;
        }
        ctx.stroke();
      } else if (ann.type === 'arrow') {
        ctx.setLineDash(ghost ? [5, 4] : []);
        const ex = ax + aw;
        const ey = ay + ah;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        ctx.setLineDash([]);
        const angle = Math.atan2(ey - ay, ex - ax);
        const hl = 14;
        ctx.beginPath();
        ctx.moveTo(ex, ey);
        ctx.lineTo(ex - hl * Math.cos(angle - Math.PI / 6), ey - hl * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(ex - hl * Math.cos(angle + Math.PI / 6), ey - hl * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
      } else if (ann.type === 'line') {
        ctx.setLineDash(ghost ? [5, 4] : []);
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(ax + aw, ay + ah);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.restore();

      // Selection outline while Move tool is active (vector / box shapes)
      if (
        showAllForMove &&
        selectedAnn === ann.id &&
        (ann.type === 'rect' ||
          ann.type === 'circle' ||
          ann.type === 'arrow' ||
          ann.type === 'line')
      ) {
        ctx.save();
        const sx = Math.min(ax, ax + aw) - 4;
        const sy = Math.min(ay, ay + ah) - 4;
        const sw = Math.abs(aw) + 8;
        const sh = Math.abs(ah) + 8;
        ctx.strokeStyle = 'rgba(255,255,255,0.95)';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(sx, sy, sw, sh);
        ctx.restore();
      }
    }

    // Live drawing preview (rect / arrow / circle / line drag)
    const drawTool = selectedToolRef.current;
    if (
      editing &&
      drawingStart &&
      drawingCurrent &&
      (drawTool === 'rect' || drawTool === 'arrow' || drawTool === 'circle' || drawTool === 'line' || drawTool === 'blur')
    ) {
      const sx = drawingStart.x * W;
      const sy = drawingStart.y * H;
      const dx = drawingCurrent.x * W;
      const dy = drawingCurrent.y * H;
      ctx.save();
      ctx.strokeStyle = '#ff4444';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      if (drawTool === 'rect' || drawTool === 'blur') {
        ctx.strokeRect(sx, sy, dx - sx, dy - sy);
      } else if (drawTool === 'circle') {
        const x0 = Math.min(sx, dx);
        const x1 = Math.max(sx, dx);
        const y0 = Math.min(sy, dy);
        const y1 = Math.max(sy, dy);
        const rcx = (x0 + x1) / 2;
        const rcy = (y0 + y1) / 2;
        const rx = Math.max((x1 - x0) / 2, 1);
        const ry = Math.max((y1 - y0) / 2, 1);
        ctx.beginPath();
        ctx.ellipse(rcx, rcy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(dx, dy);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Crosshair cursor indicator — shown only for drawing tools (not move/none)
    const tool = selectedToolRef.current;
    const hover = canvasHoverRef.current;
    if (editing && tool !== 'none' && tool !== 'move' && hover) {
      const mx = hover.x * W;
      const my = hover.y * H;
      const arm = 12;
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 1.5;
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = 3;
      ctx.beginPath();
      ctx.moveTo(mx - arm, my); ctx.lineTo(mx + arm, my);
      ctx.moveTo(mx, my - arm); ctx.lineTo(mx, my + arm);
      ctx.stroke();
      ctx.shadowBlur = 4;
      ctx.font = 'bold 11px sans-serif';
      ctx.fillStyle = '#ffffff';
      let label = '';
      if (tool === 'text') label = 'T';
      else if (tool === 'rect') label = '□';
      else if (tool === 'circle') label = '○';
      else if (tool === 'line') label = '—';
      else if (tool === 'blur') label = '▦';
      else if (tool === 'arrow') label = '→';
      else if (tool === 'image') label = '🖼';
      else if (tool === 'emoji') label = '😀';
      ctx.fillText(label, mx + arm + 4, my + 4);
      ctx.restore();
    }
  }, [drawingStart, drawingCurrent, selectedTool, selectedAnn, imageLoadTick]);

  const drawFrame = useCallback(() => {
    const vid = videoRef.current;
    const canvas = canvasRef.current;
    if (!vid || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Drawn at the video's own resolution (up to 4K; at least the screen's pixel density), so the
    // preview is sharp and the export keeps the source quality. Everything below works in display
    // units (W × H, the canvas' CSS size) — sizes in px (text, lines) look as before — and the
    // transform scales it up.
    const { clientWidth: W, clientHeight: H } = canvas;
    if (!W || !H) return;
    const scale = Math.min(Math.max(window.devicePixelRatio || 1, (vid.videoWidth || W) / W), MAX_CANVAS_WIDTH / W);
    // Even scale: the width is rounded, the height follows from it.
    const pw = Math.round(W * scale);
    const ph = Math.round((pw * H) / W);
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw;
      canvas.height = ph;
    }
    ctx.setTransform(pw / W, 0, 0, pw / W, 0, 0);

    ctx.clearRect(0, 0, W, H);

    // Video frame — during scrub/seek the decoder often reports readyState < HAVE_CURRENT_DATA; show last good frame instead of a dark placeholder
    let cache = videoFrameCacheRef.current;
    if (cache && (cache.width !== canvas.width || cache.height !== canvas.height)) {
      cache.width = canvas.width;
      cache.height = canvas.height;
    }
    let source: FrameSource | null = null;
    if (vid.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      source = vid;
      if (!cache) {
        cache = document.createElement('canvas');
        videoFrameCacheRef.current = cache;
      }
      if (cache.width !== canvas.width || cache.height !== canvas.height) {
        cache.width = canvas.width;
        cache.height = canvas.height;
      }
      const cctx = cache.getContext('2d');
      if (cctx) {
        // Contained like the live frame, so a cached frame is never stretched either.
        const r = containRect(vid.videoWidth, vid.videoHeight, 0, 0, cache.width, cache.height);
        if (r.w !== cache.width || r.h !== cache.height) {
          cctx.fillStyle = '#000000';
          cctx.fillRect(0, 0, cache.width, cache.height);
        }
        cctx.drawImage(vid, r.x, r.y, r.w, r.h);
      }
    } else if (cache && cache.width === canvas.width && cache.height === canvas.height) {
      // The cache holds the plain video frame; the frame style is applied on top.
      source = cache;
    }

    drawScene(ctx, W, H, source, vid.currentTime, !isCapturingRef.current);
  }, [drawScene]);

  /**
   * Playback jumps over removed parts: checked every frame (none of it shows) and on each time
   * update (frames don't run in a background tab). Reads refs only.
   */
  const skipRemovedPart = () => {
    const v = videoRef.current;
    if (!v || v.paused || v.seeking) return;
    const removed = removedAt(cutsRef.current, v.currentTime);
    if (!removed) return;
    if (isCapturingRef.current) {
      captureSkipRef.current?.(removed);
    } else if (removed.end >= outPointRef.current) {
      v.currentTime = outPointRef.current;
      v.pause();
    } else {
      v.currentTime = removed.end;
    }
  };

  useEffect(() => {
    const loop = () => {
      skipRemovedPart();
      drawFrame();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [drawFrame]);

  // ---------- Video events ----------

  const applyDuration = (d: number) => {
    setDuration(d);
    setOutPoint(d);
    outPointRef.current = d;
  };

  /**
   * The preview takes the video's first known size, as the export does: some recordings report it
   * only after loading (`resize`); later size changes are shown with bars, not by re-shaping.
   */
  const videoAspectSetRef = useRef(false);
  const applyVideoAspect = () => {
    const v = videoRef.current;
    if (!v || videoAspectSetRef.current || !v.videoWidth || !v.videoHeight) return;
    videoAspectSetRef.current = true;
    setVideoAspect(v.videoWidth / v.videoHeight);
  };

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    applyVideoAspect();
    if (!isFinite(v.duration)) {
      // WebM blobs recorded via MediaRecorder have Infinity duration because the container
      // header lacks a duration field. Seek far into the future: the browser will scan the
      // file, update v.duration to the real value, and fire the `seeked` event.
      fixingInfinityDurationRef.current = true;
      v.currentTime = 1e10;
      return;
    }
    applyDuration(v.duration);
  };

  const handleSeeked = () => {
    const v = videoRef.current;
    if (!v || !fixingInfinityDurationRef.current) return;
    fixingInfinityDurationRef.current = false;
    if (isFinite(v.duration)) {
      applyDuration(v.duration);
    }
    // Reset playhead to start after the forced duration-fix seek.
    v.currentTime = 0;
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    skipRemovedPart();
    const t = v.currentTime;
    setCurrentTime(t);

    const playing = !v.paused && !v.ended;
    for (const ann of annotationsRef.current) {
      if (ann.type !== 'audio' || !ann.audioSrc) continue;
      const el = videoAudioElementsRef.current.get(ann.id);
      if (!el) continue;
      el.volume = Math.max(0, Math.min(1, ann.audioVolume ?? 1));
      if (t < ann.inTime || t > ann.outTime) {
        el.pause();
        if (t < ann.inTime) el.currentTime = 0;
        continue;
      }
      const localT = Math.max(0, t - ann.inTime);
      const maxDur = ann.audioDurationSec && isFinite(ann.audioDurationSec) ? ann.audioDurationSec : el.duration;
      const clipDur = Math.max(0, Math.min(ann.outTime - ann.inTime, maxDur || 999));
      const clampedLocal = Math.min(localT, Math.max(0, clipDur - 0.05));
      if (playing) {
        if (el.paused) {
          el.currentTime = clampedLocal;
          void el.play().catch(() => {});
        } else if (Math.abs(el.currentTime - localT) > 0.35) {
          el.currentTime = clampedLocal;
        }
      } else {
        el.pause();
        el.currentTime = clampedLocal;
      }
    }

    if (isCapturingRef.current && v.currentTime >= captureEndRef.current) {
      v.pause();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
      return;
    }
    if (!isCapturingRef.current && v.currentTime > outPointRef.current) {
      v.currentTime = outPointRef.current;
      v.pause();
      setIsPlaying(false);
    }
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  // ---------- Playback controls ----------

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (v.currentTime >= outPointRef.current) v.currentTime = inPointRef.current;
      v.play();
    } else {
      v.pause();
    }
  }, []);

  const seekTo = useCallback((t: number, opts?: { syncUi?: boolean }) => {
    const v = videoRef.current;
    if (!v || !isFinite(t) || !isFinite(duration) || duration === 0) return;
    const clamped = Math.max(0, Math.min(duration, t));
    v.currentTime = clamped;
    if (opts?.syncUi) setCurrentTime(clamped);

    for (const ann of annotationsRef.current) {
      if (ann.type !== 'audio' || !ann.audioSrc) continue;
      const el = videoAudioElementsRef.current.get(ann.id);
      if (!el) continue;
      if (clamped < ann.inTime || clamped > ann.outTime) {
        el.pause();
        el.currentTime = 0;
      } else {
        el.pause();
        el.currentTime = Math.max(0, clamped - ann.inTime);
      }
    }
  }, [duration]);

  const stepFrame = useCallback((dir: 1 | -1) => {
    const v = videoRef.current;
    if (!v) return;
    seekTo(v.currentTime + dir * (1 / 30));
  }, [seekTo]);

  const getVoiceElapsedSec = useCallback(() => {
    void voiceUiTick;
    const seg = voiceSegmentClockRef.current;
    const base = voiceRecordedMsRef.current;
    const extra = seg != null ? performance.now() - seg : 0;
    return (base + extra) / 1000;
  }, [voiceUiTick]);

  const teardownVoiceStream = useCallback(() => {
    const stream = voiceStreamRef.current;
    if (stream) {
      stream.getTracks().forEach(tr => tr.stop());
      voiceStreamRef.current = null;
    }
  }, []);

  const stopVoiceMediaRecorder = useCallback(() => {
    const mr = voiceMediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
  }, []);

  const cancelVoiceReview = useCallback(() => {
    const u = voiceReviewUrl;
    if (u) URL.revokeObjectURL(u);
    voiceReviewBlobRef.current = null;
    setVoiceReviewUrl(null);
    setVoiceCapturePhase('idle');
  }, [voiceReviewUrl]);

  const insertVoiceReviewToTimeline = useCallback(() => {
    const blob = voiceReviewBlobRef.current;
    if (!blob || !voiceReviewUrl) {
      toast.warning(t('video_editor_tools.voice_review_empty'));
      return;
    }
    const url = voiceReviewUrl;
    const probe = document.createElement('audio');
    probe.preload = 'metadata';
    probe.src = url;
    probe.onloadedmetadata = () => {
      const dur = probe.duration;
      const inT = voiceRecordStartRef.current;
      const durSafe = isFinite(dur) && dur > 0 ? dur : 3;
      const maxOut = Math.min(inT + durSafe, outPointRef.current);
      const ann: Annotation = {
        id: newAnnId(),
        type: 'audio',
        x: 0.04,
        y: 0.88,
        w: 0.14,
        h: 0.07,
        text: t('video_editor_tools.voice_clip_label'),
        color: '#38bdf8',
        fontSize: 12,
        inTime: inT,
        outTime: Math.max(inT + 0.4, maxOut),
        audioSrc: url,
        audioDurationSec: durSafe,
        audioVolume: 1,
      };
      const next = [...annotationsRef.current, ann];
      setAnnotations(next);
      annotationsRef.current = next;
      pushHistory(next);
      setSelectedAnn(ann.id);
      voiceReviewBlobRef.current = null;
      setVoiceReviewUrl(null);
      setVoiceCapturePhase('idle');
    };
    probe.onerror = () => {
      const inT = voiceRecordStartRef.current;
      const ann: Annotation = {
        id: newAnnId(),
        type: 'audio',
        x: 0.04,
        y: 0.88,
        w: 0.14,
        h: 0.07,
        text: t('video_editor_tools.voice_clip_label'),
        color: '#38bdf8',
        fontSize: 12,
        inTime: inT,
        outTime: Math.min(inT + 3, outPointRef.current),
        audioSrc: url,
        audioDurationSec: 3,
        audioVolume: 1,
      };
      const next = [...annotationsRef.current, ann];
      setAnnotations(next);
      annotationsRef.current = next;
      pushHistory(next);
      setSelectedAnn(ann.id);
      voiceReviewBlobRef.current = null;
      setVoiceReviewUrl(null);
      setVoiceCapturePhase('idle');
    };
  }, [t, voiceReviewUrl]);

  const startVoiceRecording = useCallback(
    async (fromDiscardReview = false) => {
      if (voiceMediaRecorderRef.current) return;
      if (!fromDiscardReview && voiceCapturePhase !== 'idle') return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        voiceStreamRef.current = stream;
        voiceChunksRef.current = [];
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';
        const mr = new MediaRecorder(stream, { mimeType });
        voiceMediaRecorderRef.current = mr;
        voiceRecordStartRef.current = videoRef.current?.currentTime ?? 0;
        voiceDiscardOnStopRef.current = false;
        voiceRecordedMsRef.current = 0;
        voiceSegmentClockRef.current = performance.now();
        mr.ondataavailable = ev => {
          if (ev.data.size > 0) voiceChunksRef.current.push(ev.data);
        };
        mr.onstop = () => {
          const discarded = voiceDiscardOnStopRef.current;
          voiceDiscardOnStopRef.current = false;
          teardownVoiceStream();
          voiceMediaRecorderRef.current = null;

          if (discarded) {
            voiceChunksRef.current = [];
            voiceRecordedMsRef.current = 0;
            voiceSegmentClockRef.current = null;
            setVoiceCapturePhase('idle');
            return;
          }

          const blob = new Blob(voiceChunksRef.current, { type: mr.mimeType || 'audio/webm' });
          voiceChunksRef.current = [];
          voiceRecordedMsRef.current = 0;
          voiceSegmentClockRef.current = null;

          if (blob.size === 0) {
            toast.warning(t('video_editor_tools.voice_empty_blob'));
            setVoiceCapturePhase('idle');
            return;
          }
          voiceReviewBlobRef.current = blob;
          const reviewUrl = URL.createObjectURL(blob);
          setVoiceReviewUrl(reviewUrl);
          setVoiceCapturePhase('review');
        };
        mr.start();
        setVoiceCapturePhase('recording');
      } catch {
        toast.warning(t('video_editor_tools.mic_denied'));
      }
    },
    [t, teardownVoiceStream, voiceCapturePhase],
  );

  const finalizeVoiceCaptureToReview = useCallback(() => {
    if (voiceCapturePhase !== 'recording' && voiceCapturePhase !== 'paused') return;
    voiceDiscardOnStopRef.current = false;
    stopVoiceMediaRecorder();
  }, [voiceCapturePhase, stopVoiceMediaRecorder]);

  const cancelVoiceCapture = useCallback(() => {
    if (voiceCapturePhase !== 'recording' && voiceCapturePhase !== 'paused') return;
    voiceDiscardOnStopRef.current = true;
    stopVoiceMediaRecorder();
  }, [voiceCapturePhase, stopVoiceMediaRecorder]);

  const pauseVoiceCapture = useCallback(() => {
    const mr = voiceMediaRecorderRef.current;
    if (!mr || mr.state !== 'recording' || !canPauseMediaRecorder) return;
    mr.pause();
    const seg = voiceSegmentClockRef.current;
    if (seg != null) {
      voiceRecordedMsRef.current += performance.now() - seg;
      voiceSegmentClockRef.current = null;
    }
    setVoiceCapturePhase('paused');
  }, [canPauseMediaRecorder]);

  const resumeVoiceCapture = useCallback(() => {
    const mr = voiceMediaRecorderRef.current;
    if (!mr || mr.state !== 'paused') return;
    mr.resume();
    voiceSegmentClockRef.current = performance.now();
    setVoiceCapturePhase('recording');
  }, []);

  const discardReviewAndRecordAgain = useCallback(() => {
    const u = voiceReviewUrl;
    if (u) URL.revokeObjectURL(u);
    voiceReviewBlobRef.current = null;
    setVoiceReviewUrl(null);
    setVoiceCapturePhase('idle');
    void startVoiceRecording(true);
  }, [voiceReviewUrl, startVoiceRecording]);

  // ---------- Zoom segments ----------

  const selectZoom = useCallback((id: string | null) => {
    setSelectedZoom(id);
    if (id) setSelectedAnn(null);
  }, []);

  // Selecting an annotation (preview or timeline) closes the zoom settings.
  useEffect(() => {
    if (selectedAnn) setSelectedZoom(null);
  }, [selectedAnn]);

  /** Adds a zoom at the playhead (or selects the one already there). */
  const addZoomAtPlayhead = useCallback(() => {
    const v = videoRef.current;
    if (!v || !isFinite(duration) || duration < MIN_ZOOM_LENGTH) return;
    const at = v.currentTime;
    const existing = zoomsRef.current.find(z => at >= z.start && at <= z.end);
    if (existing) { selectZoom(existing.id); return; }
    const start = Math.max(0, Math.min(at, duration - MIN_ZOOM_LENGTH));
    // Up to the next zoom: zooms never overlap.
    const end = Math.min(start + DEFAULT_ZOOM_LENGTH, zoomLimits(zoomsRef.current, start, duration).hi);
    if (end - start < MIN_ZOOM_LENGTH) {
      toast.info(t('video_editor_zoom.no_room', 'No room for a zoom here — move the playhead or shorten the next zoom.'));
      return;
    }
    const zoom: ZoomSegment = {
      id: `zoom-${Date.now()}`,
      start,
      end,
      scale: DEFAULT_ZOOM_SCALE,
      x: 0.5,
      y: 0.5,
    };
    setZooms(prev => [...prev, zoom].sort((a, b) => a.start - b.start));
    selectZoom(zoom.id);
  }, [duration, selectZoom, t]);

  const updateZoom = (id: string, patch: Partial<ZoomSegment>) => {
    setZooms(prev => prev.map(z => {
      if (z.id !== id) return z;
      const next = { ...z, ...patch };
      // Timing typed in the panel stays between the neighbouring zooms.
      if ('start' in patch || 'end' in patch) {
        const { lo, hi } = zoomLimits(prev, z.start, duration, id);
        next.start = Math.max(lo, next.start);
        next.end = Math.min(hi, next.end);
        if (next.end - next.start < MIN_ZOOM_LENGTH) return z;
      }
      return next;
    }));
    // Show a look change: move a paused playhead into the zoomed part of the segment.
    const v = videoRef.current;
    const z = zoomsRef.current.find(s => s.id === id);
    const looks = 'scale' in patch || 'x' in patch || 'y' in patch;
    if (looks && v && z && v.paused && (v.currentTime <= z.start + 0.4 || v.currentTime >= z.end - 0.4)) {
      seekTo((z.start + z.end) / 2, { syncUi: true });
    }
  };

  const deleteZoom = (id: string) => {
    setZooms(prev => prev.filter(z => z.id !== id));
    if (selectedZoom === id) setSelectedZoom(null);
  };

  const selectedZoomSegment = zooms.find(z => z.id === selectedZoom) ?? null;

  // ---------- Cuts ----------

  /** Splits the video at the playhead (S). */
  const splitAtPlayhead = useCallback(() => {
    const next = addSplit(cutsRef.current, videoRef.current?.currentTime ?? 0, duration);
    if (next) changeCuts(next);
  }, [duration, changeCuts]);

  /** Removes the part under the playhead, or restores it when it is removed (Delete). */
  const togglePartAtPlayhead = useCallback(() => {
    const c = cutsRef.current;
    const at = videoRef.current?.currentTime ?? 0;
    const part = partAt(c, at, duration);
    if (removedAt(c, at)) {
      changeCuts(restorePart(c, part));
      return;
    }
    const next = removePart(c, part);
    if (!keptRanges(next, inPointRef.current, outPointRef.current).length) {
      toast.info(c.splits.length
        ? t('video_editor_cut.keep_one', 'At least one part has to stay.')
        : t('video_editor_cut.split_first', 'Split the video first (S), then delete a part.'));
      return;
    }
    changeCuts(next);
  }, [duration, changeCuts, t]);

  // ---------- Keyboard shortcuts ----------

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) return;
      // Saving: Space would pause the render mid-way and Z would add a zoom to it.
      if (showSaveModal || isCapturingRef.current) return;
      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'i':
        case 'I':
          setInPoint(videoRef.current?.currentTime ?? 0);
          inPointRef.current = videoRef.current?.currentTime ?? 0;
          break;
        case 'o':
        case 'O':
          setOutPoint(videoRef.current?.currentTime ?? duration);
          outPointRef.current = videoRef.current?.currentTime ?? duration;
          break;
        case 'ArrowLeft':
          e.preventDefault();
          stepFrame(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          stepFrame(1);
          break;
        case 'z':
          if (e.ctrlKey || e.metaKey) { e.preventDefault(); undo(); }
          else if (!e.altKey && mode === 'advanced') addZoomAtPlayhead();
          break;
        case 'y':
          if (e.ctrlKey || e.metaKey) { e.preventDefault(); redo(); }
          break;
        case 's':
        case 'S':
          if (!e.ctrlKey && !e.metaKey && !e.altKey) splitAtPlayhead();
          break;
        case 'Delete':
        case 'Backspace':
          // Only when no annotation or zoom is selected (they have their own delete buttons).
          if (selectedAnn || selectedZoom) break;
          e.preventDefault();
          togglePartAtPlayhead();
          break;
        case 'Escape':
          if (voiceCapturePhase === 'recording' || voiceCapturePhase === 'paused') {
            e.preventDefault();
            cancelVoiceCapture();
            return;
          }
          if (voiceCapturePhase === 'review') {
            e.preventDefault();
            cancelVoiceReview();
            return;
          }
          setSelectedTool('none');
          setSelectedAnn(null);
          setSelectedZoom(null);
          setPendingText(null);
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [togglePlay, stepFrame, undo, redo, duration, voiceCapturePhase, cancelVoiceCapture, cancelVoiceReview, mode, addZoomAtPlayhead, showSaveModal, splitAtPlayhead, togglePartAtPlayhead, selectedAnn, selectedZoom]);

  // ---------- Timeline annotation resize ----------

  /** Called on mousedown of a marker's left/right/center grab area. */
  const timelineTrackEl = (track: 'video' | 'audio' | 'zoom') =>
    track === 'audio' ? timelineAudioTrackRef.current
      : track === 'zoom' ? timelineZoomTrackRef.current
        : timelineTrackRef.current;

  const handleAnnResizeStart = (
    e: React.MouseEvent,
    ann: { id: string; inTime: number; outTime: number },
    edge: 'left' | 'right' | 'center',
    track: 'video' | 'audio' | 'zoom',
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const trackEl = timelineTrackEl(track);
    if (!trackEl || !isFinite(duration) || duration === 0) return;
    const rect = trackEl.getBoundingClientRect();
    const clickTime = Math.max(0, Math.min(duration, ((e.clientX - rect.left) / rect.width) * duration));
    if (track === 'zoom') selectZoom(ann.id);
    else setSelectedAnn(ann.id);
    setResizingAnn({
      id: ann.id,
      edge,
      startTime: clickTime,
      origInTime: ann.inTime,
      origOutTime: ann.outTime,
      track,
    });
  };

  // Global mouse tracking for annotation timeline resize
  useEffect(() => {
    if (!resizingAnn) return;

    const onMove = (e: MouseEvent) => {
      const trackEl = timelineTrackEl(resizingAnn.track);
      if (!trackEl || !isFinite(duration) || duration === 0) return;
      const rect = trackEl.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const time = ratio * duration;

      // `lo` / `hi`: how far the block may go (zooms stop at their neighbours).
      const resize = (inTime: number, outTime: number, lo = 0, hi = duration): [number, number] => {
        if (resizingAnn.edge === 'left') return [Math.max(lo, Math.min(time, outTime - 0.5)), outTime];
        if (resizingAnn.edge === 'right') return [inTime, Math.min(hi, Math.max(time, inTime + 0.5))];
        // Center drag — move the whole block, preserving its duration
        const annDur = resizingAnn.origOutTime - resizingAnn.origInTime;
        const delta = time - resizingAnn.startTime;
        const newIn = Math.max(lo, Math.min(hi - annDur, resizingAnn.origInTime + delta));
        return [newIn, newIn + annDur];
      };

      if (resizingAnn.track === 'zoom') {
        setZooms(prev => prev.map(z => {
          if (z.id !== resizingAnn.id) return z;
          const { lo, hi } = zoomLimits(prev, z.start, duration, z.id);
          const [start, end] = resize(z.start, z.end, lo, hi);
          return { ...z, start, end };
        }));
        return;
      }
      setAnnotations(prev => {
        const next = prev.map(a => {
          if (a.id !== resizingAnn.id) return a;
          const [inTime, outTime] = resize(a.inTime, a.outTime);
          return { ...a, inTime, outTime };
        });
        annotationsRef.current = next;
        return next;
      });
    };

    const onUp = () => {
      if (resizingAnn.track !== 'zoom') pushHistory([...annotationsRef.current]);
      setResizingAnn(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizingAnn, duration]);

  // ---------- Transport seek bar & trim scrubber ----------

  const videoTimeFromClientX = useCallback((clientX: number, el: HTMLElement | null) => {
    if (!el || !isFinite(duration) || duration === 0) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const getScrubberTime = (clientX: number) => videoTimeFromClientX(clientX, scrubberRef.current);

  const handleScrubberMouseDown = (e: React.MouseEvent, handle: 'in' | 'out' | 'playhead') => {
    e.preventDefault();
    draggingHandle.current = handle;

    const move = (ev: MouseEvent) => {
      const t = getScrubberTime(ev.clientX);
      if (draggingHandle.current === 'in') {
        const clamped = Math.min(t, outPointRef.current - 0.5);
        setInPoint(clamped);
        inPointRef.current = clamped;
      } else if (draggingHandle.current === 'out') {
        const clamped = Math.max(t, inPointRef.current + 0.5);
        setOutPoint(clamped);
        outPointRef.current = clamped;
      } else {
        seekTo(t, { syncUi: true });
      }
    };

    const up = () => {
      draggingHandle.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // ---------- Annotation interactions ----------

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (selectedTool === 'none') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    if (selectedTool === 'move') {
      const t = videoRef.current?.currentTime ?? 0;
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      const map = zoomMapping(cw, ch, frameRef.current, zoomAt(zoomsRef.current, t));
      const hit = hitTestAnnotation(
        annotationsRef.current,
        x * cw, y * ch,
        cw, ch,
        t,
        selectedAnn,
        ann => blurBoxOnScreen(ann, cw, ch, map),
      );
      if (hit) {
        e.preventDefault();
        setSelectedAnn(hit.id);
        setDraggingAnn({ id: hit.id, startX: x, startY: y, origX: hit.x, origY: hit.y });
      } else {
        setSelectedAnn(null);
      }
    } else if (selectedTool === 'image') {
      e.preventDefault();
      pendingImageRatioRef.current = { x, y };
      imageInputRef.current?.click();
    } else if (selectedTool === 'emoji') {
      e.preventDefault();
      const ann: Annotation = {
        id: newAnnId(),
        type: 'emoji',
        x,
        y,
        w: 0,
        h: 0,
        text: emojiChar,
        color: '#ffffff',
        fontSize: 48,
        inTime: videoRef.current?.currentTime ?? 0,
        outTime: Math.min((videoRef.current?.currentTime ?? 0) + 5, outPointRef.current),
      };
      const next = [...annotationsRef.current, ann];
      setAnnotations(next);
      pushHistory(next);
      setSelectedAnn(ann.id);
    } else if (selectedTool === 'text') {
      // Prevent canvas from stealing focus from the input that's about to appear.
      e.preventDefault();
      setPendingText({ x, y });
      setPendingTextValue('');
    } else {
      setDrawingStart({ x, y });
      setDrawingCurrent({ x, y });
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const pos = {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
    // Always track hover for crosshair indicator
    canvasHoverRef.current = pos;

    // Move tool — drag selected annotation
    const drag = draggingAnnRef.current;
    if (drag && selectedTool === 'move') {
      let dx = pos.x - drag.startX;
      let dy = pos.y - drag.startY;
      // A blur follows the zoom: moved by the distance on the video, not on the screen.
      const map = annotationsRef.current.find(a => a.id === drag.id)?.type === 'blur'
        ? zoomMapping(canvas.clientWidth, canvas.clientHeight, frameRef.current, zoomAt(zoomsRef.current, videoRef.current?.currentTime ?? 0))
        : null;
      if (map) {
        const now = map.toPicture(pos.x, pos.y);
        const from = map.toPicture(drag.startX, drag.startY);
        dx = now.x - from.x;
        dy = now.y - from.y;
      }
      const newAnns = annotationsRef.current.map(a =>
        a.id === drag.id
          ? { ...a, x: Math.max(0, Math.min(1, drag.origX + dx)), y: Math.max(0, Math.min(1, drag.origY + dy)) }
          : a
      );
      setAnnotations(newAnns);
      annotationsRef.current = newAnns;
      return;
    }

    // Draw tools — update end point while dragging
    if (drawingStart) {
      setDrawingCurrent(pos);
    }
  };

  const handleCanvasMouseLeave = () => {
    canvasHoverRef.current = null;
  };

  const handleCanvasMouseUp = () => {
    // Commit move drag to undo history
    if (draggingAnn) {
      pushHistory([...annotationsRef.current]);
      setDraggingAnn(null);
      return;
    }
    if (
      !drawingStart ||
      !drawingCurrent ||
      selectedTool === 'none' ||
      selectedTool === 'text' ||
      selectedTool === 'move' ||
      selectedTool === 'image' ||
      selectedTool === 'emoji'
    ) {
      setDrawingStart(null);
      setDrawingCurrent(null);
      return;
    }
    const dw = drawingCurrent.x - drawingStart.x;
    const dh = drawingCurrent.y - drawingStart.y;
    if (Math.abs(dw) < 0.01 && Math.abs(dh) < 0.01) {
      setDrawingStart(null);
      setDrawingCurrent(null);
      return;
    }
    const shapeTool = selectedTool as DrawToolType;
    let start = drawingStart;
    let end = drawingCurrent;
    const canvas = canvasRef.current;
    if (shapeTool === 'blur' && canvas) {
      // Drawn while zoomed: stored where it is on the video (it follows the zoom).
      const map = zoomMapping(canvas.clientWidth, canvas.clientHeight, frameRef.current, zoomAt(zoomsRef.current, videoRef.current?.currentTime ?? 0));
      if (map) {
        start = map.toPicture(start.x, start.y);
        end = map.toPicture(end.x, end.y);
      }
    }
    const ann: Annotation = {
      id: newAnnId(),
      type: shapeTool,
      x: start.x,
      y: start.y,
      w: end.x - start.x,
      h: end.y - start.y,
      text: '',
      color: '#ff4444',
      fontSize: 20,
      inTime: videoRef.current?.currentTime ?? 0,
      outTime: Math.min((videoRef.current?.currentTime ?? 0) + 5, outPointRef.current),
      ...(shapeTool === 'blur' ? { blurStyle: 'pixelate' as const, blurStrength: BLUR_STRENGTH.default } : {}),
    };
    const next = [...annotationsRef.current, ann];
    setAnnotations(next);
    pushHistory(next);
    setDrawingStart(null);
    setDrawingCurrent(null);
    setSelectedAnn(ann.id);
  };

  const confirmPendingText = () => {
    if (!pendingText || !pendingTextValue.trim()) {
      setPendingText(null);
      return;
    }
    const ann: Annotation = {
      id: newAnnId(),
      type: 'text',
      x: pendingText.x,
      y: pendingText.y,
      w: 0,
      h: 0,
      text: pendingTextValue,
      color: '#ffffff',
      fontSize: 20,
      fontFamily: 'Serif',
      backgroundColor: 'rgba(0,0,0,0.82)',
      bold: false,
      italic: false,
      underline: false,
      textAlign: 'left',
      textBoxWidthRatio: 0.45,
      inTime: videoRef.current?.currentTime ?? 0,
      outTime: Math.min((videoRef.current?.currentTime ?? 0) + 5, outPointRef.current),
    };
    const next = [...annotationsRef.current, ann];
    setAnnotations(next);
    pushHistory(next);
    setPendingText(null);
    setPendingTextValue('');
    setSelectedAnn(ann.id);
  };

  const deleteAnnotation = (id: string) => {
    const next = annotationsRef.current.filter(a => a.id !== id);
    annotationsRef.current = next;
    setAnnotations(next);
    pushHistory(next);
    if (selectedAnn === id) setSelectedAnn(null);
  };

  const duplicateAnnotation = (id: string) => {
    const src = annotationsRef.current.find(a => a.id === id);
    if (!src) return;
    const dup: Annotation = {
      ...src,
      id: newAnnId(),
      x: Math.min(0.92, src.x + 0.03),
      y: Math.min(0.92, src.y + 0.03),
    };
    const next = [...annotationsRef.current, dup];
    setAnnotations(next);
    annotationsRef.current = next;
    pushHistory(next);
    setSelectedAnn(dup.id);
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const pos = pendingImageRatioRef.current;
    e.target.value = '';
    pendingImageRatioRef.current = null;
    if (!file || !file.type.startsWith('image/') || !pos) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const w = 0.22;
      const h = 0.22;
      const ann: Annotation = {
        id: newAnnId(),
        type: 'image',
        x: Math.max(0, Math.min(1 - w, pos.x - w / 2)),
        y: Math.max(0, Math.min(1 - h, pos.y - h / 2)),
        w,
        h,
        text: '',
        color: '#ffffff',
        fontSize: 20,
        imageSrc: dataUrl,
        inTime: videoRef.current?.currentTime ?? 0,
        outTime: Math.min((videoRef.current?.currentTime ?? 0) + 5, outPointRef.current),
      };
      const next = [...annotationsRef.current, ann];
      setAnnotations(next);
      annotationsRef.current = next;
      pushHistory(next);
      setSelectedAnn(ann.id);
    };
    reader.readAsDataURL(file);
  };

  // ---------- Save As — hydrate draft ----------

  useEffect(() => {
    if (!initialDraftProject || draftHydratedRef.current) return;
    draftHydratedRef.current = true;
    setInPoint(initialDraftProject.inPoint);
    setOutPoint(initialDraftProject.outPoint);
    inPointRef.current = initialDraftProject.inPoint;
    outPointRef.current = initialDraftProject.outPoint;
    setMode(initialDraftProject.mode);
    setTimelineZoom(initialDraftProject.timelineZoom);
    setFrame(normalizeFrame(initialDraftProject.frame));
    setZooms(normalizeZooms(initialDraftProject.zooms));
    // Ensure the restored draft name also has the copy suffix to avoid overwriting the original blob.
    const draftName = initialDraftProject.saveFileName || '';
    const originalTitle = video.title || 'edited-video';
    const hasCopySuffix = /\s*\((?:copia|copy)\s*\d+\)$/i.test(draftName);
    setSaveFileName(hasCopySuffix ? draftName : copyName(draftName || originalTitle));
    const anns = initialDraftProject.annotations.map(a => ({ ...a })) as Annotation[];
    setAnnotations(anns);
    annotationsRef.current = anns;
    const draftCuts = normalizeCuts(initialDraftProject.cuts);
    setCuts(draftCuts);
    cutsRef.current = draftCuts;
    historyStack.current = [{ anns: anns.map(a => ({ ...a })), cuts: draftCuts }];
    historyIdx.current = 0;
  }, [initialDraftProject]);

  const buildProjectSnapshot = useCallback((): VideoEditDraftProject => ({
    version: VIDEO_EDIT_DRAFT_SCHEMA_VERSION,
    inPoint: inPointRef.current,
    outPoint: outPointRef.current,
    annotations: annotationsRef.current.map(a => ({ ...a })),
    mode,
    timelineZoom,
    saveFileName: saveFileNameRef.current,
    frame,
    zooms,
    cuts: cutsRef.current,
  }), [mode, timelineZoom, frame, zooms]);

  const handleSaveDraft = async () => {
    if (!user?.email) {
      toast.warning(t('video_editor_save.sign_in_to_save'));
      return;
    }
    const annsForStorage = await Promise.all(
      annotationsRef.current.map(async a => {
        let imageSrc = a.imageSrc;
        let audioSrc = a.audioSrc;
        if (a.type === 'image' && imageSrc?.startsWith('blob:')) {
          const next = await blobUrlToDataUrl(imageSrc);
          URL.revokeObjectURL(imageSrc);
          imageSrc = next;
        }
        if (a.type === 'audio' && audioSrc?.startsWith('blob:')) {
          const next = await blobUrlToDataUrl(audioSrc);
          URL.revokeObjectURL(audioSrc);
          audioSrc = next;
        }
        return { ...a, imageSrc, audioSrc };
      }),
    );
    setAnnotations(annsForStorage);
    annotationsRef.current = annsForStorage;

    const slug = localStorage.getItem('slug') || 'default';
    const id = activeDraftId || `draft-${Date.now()}`;
    const now = new Date().toISOString();
    const existing = activeDraftId
      ? getVideoEditDraft(user.email, slug, activeDraftId)
      : null;
    const draft: VideoEditDraft = {
      id,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      title: saveFileNameRef.current.trim() || video.title || 'Draft',
      source: {
        id: video.id,
        title: video.title,
        description: video.description,
        url: video.url,
        streamUrl: video.streamUrl,
        file_url: video.file_url,
        duration: duration || video.duration,
      },
      project: {
        version: VIDEO_EDIT_DRAFT_SCHEMA_VERSION,
        inPoint: inPointRef.current,
        outPoint: outPointRef.current,
        annotations: annsForStorage,
        mode,
        timelineZoom,
        saveFileName: saveFileNameRef.current,
        frame,
        zooms,
        cuts: cutsRef.current,
      },
    };
    upsertVideoEditDraft(user.email, slug, draft);
    setActiveDraftId(id);
    toast.success(t('video_editor_save.draft_saved'));
    setShowSaveModal(false);
  };

  // ---------- Save As — canvas capture ----------

  const stopOverlaySources = () => {
    overlaySourcesRef.current.forEach(src => {
      try { src.stop(); } catch { /* not started or already stopped */ }
    });
    overlaySourcesRef.current = [];
  };

  // Real-time capture: draws while the tab is in the background (requestAnimationFrame stops there).
  const stopCaptureTickerRef = useRef<(() => void) | null>(null);
  useEffect(() => () => stopCaptureTickerRef.current?.(), []);
  const resetCapture = () => {
    stopCaptureTickerRef.current?.();
    stopCaptureTickerRef.current = null;
    captureSkipRef.current = null;
    pendingSaveKindRef.current = null;
    setCaptureKind(null);
    isCapturingRef.current = false;
    setIsCapturing(false);
    setCaptureProgress(0);
  };

  /** Downloads the exported video, or uploads it to My Library. */
  const deliverExport = async (k: 'download' | 'library' | null, blob: Blob) => {
    const safeBase = saveFileNameRef.current.replace(/[/\\?%*:|"<>]/g, '-');

    const isMp4 = blob.type.includes('mp4');
    if (k === 'download') {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeBase}.${isMp4 ? 'mp4' : 'webm'}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setShowSaveModal(false);
    } else if (k === 'library') {
      setIsUploading(true);
      try {
        // Use the editor's trim points as the authoritative duration to avoid
        // the WebM Infinity-duration bug (MediaRecorder output lacks duration headers).
        const exportedDuration = totalLength(keptRanges(cutsRef.current, inPointRef.current, outPointRef.current));
        // The copy keeps the video's description (a default note when it has none).
        const description = richTextToPlain(video.description || '').trim()
          ? video.description as string
          : t('video_editor_save.upload_description');
        const result = await uploadVideo(
          blob,
          safeBase,
          description,
          exportedDuration > 0 ? exportedDuration : undefined,
        );
        if (result.status === 'storage_exceeded') {
          setStorageInfo(await getStorageInfo());
          setStorageExceeded(true);
        } else if (result.status === 'success') {
          setShowSaveModal(false);
          // Library saves are always MP4: a WebM (real-time fallback) is converted on the server.
          if (!isMp4 && result.video?.id) {
            // The library tile shows "Saving as MP4 · %" and announces the end.
            const { error } = await apiClient.startVideoJob(result.video.id, 'mp4', { replace: true });
            if (error) toast.warning(t('video_editor_save.mp4_not_started', 'Saved to My Library as WebM — the MP4 conversion could not start.'));
            else {
              toast.info(t('video_editor_save.upload_ok_mp4', 'Saved to My Library — converting to MP4…'), { id: saveAsMp4ToastId(result.video.id) });
              // The library was refreshed by the upload, before the job existed.
              broadcastVideosRefresh();
              followSaveAsMp4(result.video.id);
            }
          } else {
            toast.success(t('video_editor_save.upload_ok'));
          }
        } else {
          toast.error(t('video_editor_save.upload_fail'));
        }
      } catch {
        toast.error(t('video_editor_save.upload_fail'));
      } finally {
        setIsUploading(false);
      }
    }
  };

  /** Stops a running render; nothing is downloaded or uploaded. */
  const cancelCapture = () => {
    if (!isCapturingRef.current) return;
    captureCancelledRef.current = true;
    fastExportAbortRef.current?.abort();
    videoRef.current?.pause();
    stopOverlaySources();
    const mr = mediaRecorderRef.current;
    // Still preparing (no recorder yet): startCapture stops itself at its next check.
    if (mr && mr.state !== 'inactive') mr.stop();
  };

  const startCapture = async (kind: 'download' | 'library') => {
    const v = videoRef.current;
    const canvas = canvasRef.current;
    if (!v || !canvas) return;
    if (kind === 'library' && !user) {
      toast.warning(t('video_editor_save.sign_in_to_save'));
      return;
    }
    // The trim without the removed parts.
    const ranges = keptRanges(cutsRef.current, inPointRef.current, outPointRef.current);
    if (!ranges.length) {
      toast.warning(t('video_editor_cut.nothing_kept', 'Nothing left to save: restore a part or change the trim.'));
      return;
    }

    pendingSaveKindRef.current = kind;
    captureCancelledRef.current = false;
    overlaySourcesRef.current = [];
    setCaptureKind(kind);
    setIsCapturing(true);
    isCapturingRef.current = true;
    captureEndRef.current = ranges[ranges.length - 1].end;
    chunksRef.current = [];

    // Fast export (decode → draw → encode, no playback); the real-time capture below is the
    // fallback for browsers without WebCodecs or files it can't read.
    const fast = new AbortController();
    fastExportAbortRef.current = fast;
    setFastRender(true);
    // Loaded on first save only (keeps the app's initial download small).
    let unsupported: unknown = null;
    try {
      const { fastExport, FastExportUnsupported } = await import('../lib/fastExport');
      unsupported = FastExportUnsupported;
      let shown = 0;
      const blob = await fastExport({
        sourceUrl: video.playbackUrl || videoSrc,
        ranges,
        width: canvas.clientWidth,
        draw: (ctx, W, H, source, t) => drawScene(ctx, W, H, source, t, false),
        originalVolume: muteOriginalAudio ? 0 : 1,
        overlays: annotationsRef.current
          .filter(a => a.type === 'audio' && a.audioSrc)
          .map(a => ({ src: a.audioSrc as string, inTime: a.inTime, outTime: a.outTime, volume: Math.max(0, Math.min(1, a.audioVolume ?? 1)) })),
        signal: fast.signal,
        onProgress: (p) => {
          if (p - shown >= 0.01 || p >= 1) { shown = p; setCaptureProgress(p); }
        },
      });
      const k = pendingSaveKindRef.current;
      resetCapture();
      await deliverExport(k, blob);
      return;
    } catch (error) {
      if (captureCancelledRef.current || (error as Error)?.name === 'AbortError') {
        resetCapture();
        toast.info(t('video_editor_save.cancelled', 'Save cancelled.'));
        return;
      }
      if (!(unsupported && error instanceof (unsupported as typeof Error))) {
        console.warn('[VideoEditor] Fast export failed; using real-time capture.', error);
      }
      setCaptureProgress(0);
    } finally {
      fastExportAbortRef.current = null;
      setFastRender(false);
    }

    // Audio setup — create once
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
      audioSrcRef.current = audioCtxRef.current.createMediaElementSource(v);
      audioDestRef.current = audioCtxRef.current.createMediaStreamDestination();
      // GainNode controls the original audio level for both export and preview.
      // NOTE: once createMediaElementSource is called, v.muted has no effect —
      // all audio must go through the AudioContext graph to reach the speakers.
      originalAudioGainRef.current = audioCtxRef.current.createGain();
      originalAudioGainRef.current.gain.value = muteOriginalAudio ? 0 : 1;
      audioSrcRef.current.connect(originalAudioGainRef.current);
      originalAudioGainRef.current.connect(audioDestRef.current);      // → recording
      originalAudioGainRef.current.connect(audioCtxRef.current.destination); // → speakers (preview)
    }

    const ctx = audioCtxRef.current;
    const dest = audioDestRef.current;
    if (!dest) {
      setIsCapturing(false);
      isCapturingRef.current = false;
      setCaptureKind(null);
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : 'video/webm';

    v.currentTime = ranges[0].start;
    await new Promise<void>(res => {
      const onSeeked = () => {
        v.removeEventListener('seeked', onSeeked);
        res();
      };
      v.addEventListener('seeked', onSeeked);
    });

    await ctx.resume();

    const trimStart = ranges[0].start;
    const trimEnd = captureEndRef.current;

    // Voice-overs, decoded first; each kept range plays its part of them (scheduleOverlays).
    const clips: { buffer: AudioBuffer; inTime: number; outTime: number; volume: number }[] = [];
    for (const ann of annotationsRef.current) {
      if (ann.type !== 'audio' || !ann.audioSrc) continue;
      if (Math.min(ann.outTime, trimEnd) - Math.max(ann.inTime, trimStart) < 0.05) continue;
      try {
        const resFetch = await fetch(ann.audioSrc);
        const ab = await resFetch.arrayBuffer();
        const buffer = await ctx.decodeAudioData(ab.slice(0));
        clips.push({ buffer, inTime: ann.inTime, outTime: ann.outTime, volume: Math.max(0, Math.min(1, ann.audioVolume ?? 1)) });
      } catch {
        /* skip broken overlay clip */
      }
    }
    /** Starts the voice-overs from source time `from` to the end of its kept range. */
    const scheduleOverlays = (from: number) => {
      const range = ranges.find(r => from >= r.start - 1e-3 && from < r.end);
      if (!range) return;
      const baseWhen = ctx.currentTime + 0.06;
      for (const clip of clips) {
        const audibleStart = Math.max(clip.inTime, from);
        const audibleEnd = Math.min(clip.outTime, range.end, clip.inTime + clip.buffer.duration);
        if (audibleEnd - audibleStart < 0.05) continue;
        const bufSrc = ctx.createBufferSource();
        bufSrc.buffer = clip.buffer;
        const g = ctx.createGain();
        g.gain.value = clip.volume;
        bufSrc.connect(g);
        g.connect(dest);
        g.connect(ctx.destination);
        bufSrc.start(baseWhen + (audibleStart - from), audibleStart - clip.inTime, audibleEnd - audibleStart);
        overlaySourcesRef.current.push(bufSrc);
      }
    };

    // Cancelled while preparing: start nothing.
    if (captureCancelledRef.current) {
      stopOverlaySources();
      resetCapture();
      return;
    }

    const videoStream = canvas.captureStream(30);
    const audioTracks = dest.stream.getAudioTracks() ?? [];
    audioTracks.forEach(track => videoStream.addTrack(track));

    // The browser default (~2.5 Mbps) blurs text at full resolution: ~4 bits per pixel per second
    // (about 8 Mbps at 1080p), between 4 and 16 Mbps.
    const videoBitsPerSecond = Math.round(Math.min(16e6, Math.max(4e6, canvas.width * canvas.height * 4)));
    const mr = new MediaRecorder(videoStream, { mimeType, videoBitsPerSecond });
    mediaRecorderRef.current = mr;

    mr.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mr.onstop = () => {
      void (async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const k = pendingSaveKindRef.current;
        resetCapture();
        if (captureCancelledRef.current) {
          chunksRef.current = [];
          toast.info(t('video_editor_save.cancelled', 'Save cancelled.'));
          return;
        }

        await deliverExport(k, blob);
      })();
    };

    // A removed part: recording pauses while the video jumps over it.
    captureSkipRef.current = (removed) => {
      const rec = mediaRecorderRef.current;
      stopOverlaySources();
      if (removed.end >= captureEndRef.current) {
        v.pause();
        if (rec && rec.state !== 'inactive') rec.stop();
        return;
      }
      if (rec?.state === 'recording') rec.pause();
      v.currentTime = removed.end;
      v.addEventListener('seeked', () => {
        if (!isCapturingRef.current || captureCancelledRef.current) return;
        scheduleOverlays(removed.end);
        if (rec?.state === 'paused') rec.resume();
      }, { once: true });
    };

    // Worker timers keep running in a background tab; the frame loop (requestAnimationFrame) doesn't.
    const tickerUrl = URL.createObjectURL(new Blob(['setInterval(() => postMessage(0), 33);'], { type: 'text/javascript' }));
    const ticker = new Worker(tickerUrl);
    ticker.onmessage = () => { if (document.hidden) drawFrame(); };
    stopCaptureTickerRef.current = () => { ticker.terminate(); URL.revokeObjectURL(tickerUrl); };
    mr.start(100);
    scheduleOverlays(trimStart);
    v.play();
    setIsPlaying(true);

    // Track progress (time in the export)
    const range = totalLength(ranges);
    const interval = setInterval(() => {
      if (!isCapturingRef.current) {
        clearInterval(interval);
        return;
      }
      setCaptureProgress(Math.min(1, exportTimeAt(ranges, v.currentTime) / range));
    }, 200);
  };

  // ---------- Computed values ----------

  const inPct = duration > 0 ? (inPoint / duration) * 100 : 0;
  const pctOf = (time: number) => (duration > 0 ? (time / duration) * 100 : 0);
  // Cuts: what the export keeps, and the part under the playhead (what Split / Delete act on).
  const keptLength = totalLength(keptRanges(cuts, inPoint, outPoint));
  const removedCount = cuts.removed.filter(r => r.end > inPoint && r.start < outPoint).length;
  const playheadRemoved = !!removedAt(cuts, currentTime);
  const currentPart = cuts.splits.length ? partAt(cuts, currentTime, duration) : null;
  const outPct = duration > 0 ? (outPoint / duration) * 100 : 100;
  const playPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const videoSrc = video.url || video.streamUrl || video.file_url || '';
  const canUndo = historyIdx.current > 0;
  const canRedo = historyIdx.current < historyStack.current.length - 1;

  const visualTimelineAnnotations = useMemo(
    () => annotations.filter(a => a.type !== 'audio'),
    [annotations],
  );
  const audioTimelineAnnotations = useMemo(
    () => annotations.filter(a => a.type === 'audio'),
    [annotations],
  );

  // ---------- Render ----------

  return (
    <div className="ve-root" ref={veRootRef}>
      {/* ── Header ── */}
      <header className="ve-header">
        <div className="ve-header-left">
          <button className="ve-icon-btn" onClick={onClose} aria-label={t('video_editor_ui.close', 'Close editor')}>
            <X size={18} />
          </button>
          <nav className="ve-breadcrumb" aria-label={t('video_editor_ui.location', 'Editor location')}>
            <span>{t('my_library', 'My Library')}</span>
            <ChevronRight size={12} aria-hidden />
            <span className="ve-breadcrumb-active">{video.title}</span>
          </nav>
        </div>

        <div className="ve-header-center">
          <div className="ve-mode-toggle" role="group" aria-label={t('video_editor_ui.mode', 'Editor mode')}>
            <button
              className={`ve-mode-btn${mode === 'basic' ? ' active' : ''}`}
              onClick={() => setMode('basic')}
            >
              {t('video_editor_ui.basic', 'Basic')}
            </button>
            <button
              className={`ve-mode-btn${mode === 'advanced' ? ' active' : ''}`}
              onClick={() => setMode('advanced')}
            >
              <Layers size={13} aria-hidden />
              {t('video_editor_ui.advanced', 'Advanced')}
            </button>
          </div>
        </div>

        <div className="ve-header-right">
          <button
            className="ve-icon-btn"
            onClick={undo}
            aria-label={t('video_editor_ui.undo', 'Undo')}
            disabled={!canUndo}
            title={t('video_editor_ui.undo_title', 'Undo (Ctrl+Z)')}
          >
            <Undo2 size={16} />
          </button>
          <button
            className="ve-icon-btn"
            onClick={redo}
            aria-label={t('video_editor_ui.redo', 'Redo')}
            disabled={!canRedo}
            title={t('video_editor_ui.redo_title', 'Redo (Ctrl+Y)')}
          >
            <Redo2 size={16} />
          </button>
          <button
            type="button"
            className="ve-icon-btn"
            onClick={() => void toggleEditorFullscreen()}
            aria-label={
              editorIsFullscreen
                ? t('video_editor_chrome.fullscreen_exit')
                : t('video_editor_chrome.fullscreen_enter')
            }
            aria-pressed={editorIsFullscreen}
            title={
              editorIsFullscreen
                ? t('video_editor_chrome.fullscreen_exit')
                : t('video_editor_chrome.fullscreen_enter')
            }
          >
            {editorIsFullscreen ? <Minimize2 size={16} aria-hidden /> : <Maximize2 size={16} aria-hidden />}
          </button>
          <div className="ve-shortcuts" ref={shortcutsRef}>
            <button
              type="button"
              className={`ve-icon-btn${showShortcuts ? ' active' : ''}`}
              onClick={() => setShowShortcuts((v) => !v)}
              aria-expanded={showShortcuts}
              aria-label={t('video_editor_ui.shortcuts', 'Keyboard shortcuts')}
              title={t('video_editor_ui.shortcuts', 'Keyboard shortcuts')}
            >
              <Keyboard size={16} aria-hidden />
            </button>
            {showShortcuts && (
              <div className="ve-shortcuts-pop" role="dialog" aria-label={t('video_editor_ui.shortcuts', 'Keyboard shortcuts')}>
                {[
                  ['Space', t('video_editor_ui.key_play', 'play/pause')],
                  ['I', t('video_editor_ui.key_in', 'set in')],
                  ['O', t('video_editor_ui.key_out', 'set out')],
                  ['S', t('video_editor_cut.split', 'Split')],
                  ['← →', t('video_editor_ui.key_frame', 'frame step')],
                  ['Z', t('video_editor_ui.key_zoom', 'zoom')],
                  ['Ctrl+Z', t('video_editor_ui.key_undo', 'undo')],
                  ['Ctrl+Y', t('video_editor_ui.redo', 'Redo')],
                ].map(([key, label]) => (
                  <div key={key} className="ve-shortcuts-row">
                    <kbd>{key}</kbd>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            className="ve-btn-primary"
            onClick={() => setShowSaveModal(true)}
            disabled={isCapturing}
          >
            <Save size={14} aria-hidden />
            {t('video_editor_save.title')}
          </button>
        </div>
      </header>
      <p className="ve-small-screen-note" role="note">
        {t('video_editor_ui.small_screen', 'Editing works best on a larger screen.')}
      </p>

      {/* ── Body ── */}
      <div className="ve-body">
        {/* Annotation toolbar — advanced only */}
        {mode === 'advanced' && (
          <aside className="ve-ann-toolbar" role="toolbar" aria-label={t('video_editor_ui.annotation_tools', 'Annotation tools')}>
            {/* Move tool — first so it's the default action */}
            <button
              className={`ve-icon-btn${selectedTool === 'move' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'move' ? 'none' : 'move')}
              aria-label={t('video_editor_ui.tool_move', 'Move annotation')}
              aria-pressed={selectedTool === 'move'}
              title={t('video_editor_ui.tool_move_title', 'Move annotation (click & drag)')}
            >
              <Move size={16} />
            </button>

            <div className="ve-toolbar-divider" aria-hidden />

            <button
              className={`ve-icon-btn${selectedTool === 'text' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'text' ? 'none' : 'text')}
              aria-label={t('video_editor_ui.tool_text', 'Add text annotation')}
              aria-pressed={selectedTool === 'text'}
              title={t('video_editor_ui.tool_text_title', 'Text (click on video)')}
            >
              <Type size={16} />
            </button>
            <button
              className={`ve-icon-btn${selectedTool === 'rect' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'rect' ? 'none' : 'rect')}
              aria-label={t('video_editor_ui.tool_rect', 'Add rectangle annotation')}
              aria-pressed={selectedTool === 'rect'}
              title={t('video_editor_ui.tool_rect_title', 'Rectangle (drag on video)')}
            >
              <Square size={16} />
            </button>
            <button
              className={`ve-icon-btn${selectedTool === 'blur' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'blur' ? 'none' : 'blur')}
              aria-label={t('video_editor_blur.tool', 'Blur an area')}
              aria-pressed={selectedTool === 'blur'}
              title={t('video_editor_blur.tool_hint', 'Blur (drag over what to hide, e.g. an email or password)')}
            >
              <EyeOff size={16} />
            </button>
            <button
              className={`ve-icon-btn${selectedTool === 'arrow' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'arrow' ? 'none' : 'arrow')}
              aria-label={t('video_editor_tools.tool_arrow')}
              aria-pressed={selectedTool === 'arrow'}
              title={t('video_editor_tools.tool_arrow_title')}
            >
              <ArrowRight size={16} />
            </button>
            <button
              className={`ve-icon-btn${selectedTool === 'circle' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'circle' ? 'none' : 'circle')}
              aria-label={t('video_editor_tools.tool_circle')}
              aria-pressed={selectedTool === 'circle'}
              title={t('video_editor_tools.tool_circle_title')}
            >
              <Circle size={16} />
            </button>
            <button
              className={`ve-icon-btn${selectedTool === 'line' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'line' ? 'none' : 'line')}
              aria-label={t('video_editor_tools.tool_line')}
              aria-pressed={selectedTool === 'line'}
              title={t('video_editor_tools.tool_line_title')}
            >
              <Minus size={16} />
            </button>
            <button
              className={`ve-icon-btn${selectedTool === 'image' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'image' ? 'none' : 'image')}
              aria-label={t('video_editor_tools.tool_image')}
              aria-pressed={selectedTool === 'image'}
              title={t('video_editor_tools.tool_image_title')}
            >
              <ImagePlus size={16} />
            </button>
            <button
              className={`ve-icon-btn${selectedTool === 'emoji' ? ' active' : ''}`}
              onClick={() => setSelectedTool(t => t === 'emoji' ? 'none' : 'emoji')}
              aria-label={t('video_editor_tools.tool_emoji')}
              aria-pressed={selectedTool === 'emoji'}
              title={t('video_editor_tools.tool_emoji_title')}
            >
              <Smile size={16} />
            </button>

            <div className="ve-toolbar-divider" aria-hidden />

            <label className="ve-emoji-preset-label" htmlFor="ve-emoji-preset">
              {t('video_editor_tools.emoji_preset')}
              <select
                id="ve-emoji-preset"
                className="ve-emoji-preset-select"
                value={emojiChar}
                onChange={e => setEmojiChar(e.target.value)}
              >
                {['😀', '👍', '❤️', '⭐', '🎉', '✅', '📌', '🔔', '😂', '🙏'].map(em => (
                  <option key={em} value={em}>{em}</option>
                ))}
              </select>
            </label>

            <div className="ve-toolbar-divider" aria-hidden />

            <button
              type="button"
              disabled={voiceCapturePhase === 'review'}
              className={`ve-icon-btn${
                voiceCapturePhase === 'recording' || voiceCapturePhase === 'paused' ? ' active ve-recording-pulse' : ''
              }`}
              onClick={() => {
                if (voiceCapturePhase === 'idle') void startVoiceRecording(false);
                else if (voiceCapturePhase === 'recording' || voiceCapturePhase === 'paused') {
                  finalizeVoiceCaptureToReview();
                }
              }}
              aria-label={
                voiceCapturePhase === 'recording' || voiceCapturePhase === 'paused'
                  ? t('video_editor_tools.finish_capture')
                  : t('video_editor_tools.record_audio')
              }
              aria-pressed={voiceCapturePhase === 'recording' || voiceCapturePhase === 'paused'}
              title={
                voiceCapturePhase === 'review'
                  ? t('video_editor_tools.record_audio_disabled_review')
                  : voiceCapturePhase === 'recording' || voiceCapturePhase === 'paused'
                    ? t('video_editor_tools.finish_capture_hint')
                    : t('video_editor_tools.record_audio_title')
              }
            >
              <Mic size={16} />
            </button>

            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="ve-hidden-file-input"
              onChange={handleImageFileChange}
              aria-hidden
              tabIndex={-1}
            />

          </aside>
        )}

        {/* ── Player + controls ── */}
        <main className="ve-player-area">
          <div className="ve-preview-row">
            <div className="ve-preview-main">
              <div className="ve-canvas-wrap" ref={containerRef}>
                {mode === 'advanced' && (selectedTool !== 'none' || voiceCapturePhase !== 'idle') ? (
                  <p className="ve-tool-hint" aria-live="polite">
                    {voiceCapturePhase === 'recording'
                      ? `${t('video_editor_tools.recording')} (${formatVoiceElapsed(getVoiceElapsedSec())})`
                      : voiceCapturePhase === 'paused'
                        ? `${t('video_editor_tools.voice_paused')} (${formatVoiceElapsed(getVoiceElapsedSec())})`
                        : voiceCapturePhase === 'review'
                          ? t('video_editor_tools.voice_review_hint')
                          : selectedTool === 'move'
                            ? t('video_editor_tools.hint_move')
                            : selectedTool === 'text'
                              ? t('video_editor_tools.hint_text')
                              : selectedTool === 'image'
                                ? t('video_editor_tools.hint_image')
                                : selectedTool === 'emoji'
                                  ? t('video_editor_tools.hint_emoji')
                                  : t('video_editor_tools.hint_drag')}
                  </p>
                ) : null}
                <video
                  ref={videoRef}
                  src={videoSrc}
                  onLoadStart={() => { videoAspectSetRef.current = false; }}
                  onLoadedMetadata={handleLoadedMetadata}
                  onResize={applyVideoAspect}
                  onSeeked={handleSeeked}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                  playsInline
                  preload="metadata"
                  crossOrigin="anonymous"
                />
                <canvas
                  ref={canvasRef}
                  className="ve-canvas"
                  style={{
                    width: `min(100cqw, ${(100 * videoAspect).toFixed(4)}cqh)`,
                    height: `min(100cqh, ${(100 / videoAspect).toFixed(4)}cqw)`,
                    cursor: draggingAnn
                      ? 'grabbing'
                      : selectedTool === 'move'
                      ? 'grab'
                      : selectedTool !== 'none'
                      ? 'crosshair'
                      : 'default',
                  }}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseLeave}
                />

                {pendingText && (
                  <div
                    className="ve-text-overlay"
                    // On the video (centred in the preview area, with its own proportions).
                    style={{
                      left: (canvasRef.current?.offsetLeft ?? 0) + Math.min(pendingText.x, 0.68) * (canvasRef.current?.offsetWidth ?? 0),
                      top: (canvasRef.current?.offsetTop ?? 0) + Math.min(pendingText.y, 0.82) * (canvasRef.current?.offsetHeight ?? 0),
                    }}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    <input
                      ref={textInputRef}
                      className="ve-text-input"
                      value={pendingTextValue}
                      onChange={e => setPendingTextValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); confirmPendingText(); }
                        if (e.key === 'Escape') { e.preventDefault(); setPendingText(null); }
                      }}
                      placeholder={t('video_editor_ui.text_placeholder', 'Type, then ↵ Enter')}
                      aria-label={t('video_editor_ui.text_aria', 'Annotation text')}
                    />
                    <button
                      className="ve-text-confirm-btn"
                      onClick={confirmPendingText}
                      onMouseDown={e => e.preventDefault()}
                      aria-label={t('video_editor_ui.tool_text', 'Add text annotation')}
                      title={t('video_editor_ui.text_add_title', 'Add annotation (Enter)')}
                    >
                      ✓
                    </button>
                  </div>
                )}
              </div>
            </div>

            {mode === 'advanced' && selectedZoomSegment ? (
              <aside className="ve-props-panel" aria-label={t('video_editor_zoom.title', 'Zoom')}>
                <VideoZoomPanel
                  zoom={selectedZoomSegment}
                  aspect={videoAspect}
                  duration={duration}
                  onChange={patch => updateZoom(selectedZoomSegment.id, patch)}
                  onDelete={() => deleteZoom(selectedZoomSegment.id)}
                />
              </aside>
            ) : mode === 'advanced' ? (
              <VideoEditorPropertiesPanel
                selected={selectedForProperties}
                onApplyPatch={(id, p) => applyAnnPatch(id, p as Partial<Annotation>)}
                onCommitHistory={commitAnnotationsHistory}
                onDelete={deleteAnnotation}
                onDuplicate={duplicateAnnotation}
                emptyContent={<VideoFramePanel frame={frame} onChange={setFrame} />}
                timing={selectedTiming}
              />
            ) : (
              <aside className="ve-props-panel" aria-label={t('video_editor_frame.title', 'Frame')}>
                <VideoFramePanel frame={frame} onChange={setFrame} />
              </aside>
            )}
          </div>

          {/* Playback controls */}
          <div className="ve-controls">
            <button
              className="ve-icon-btn"
              onClick={() => stepFrame(-1)}
              aria-label={t('video_editor_ui.prev_frame', 'Previous frame (←)')}
              title={t('video_editor_ui.prev_frame', 'Previous frame (←)')}
            >
              <SkipBack size={16} />
            </button>
            <button
              className="ve-play-btn"
              onClick={togglePlay}
              aria-label={isPlaying ? t('video_editor_ui.pause', 'Pause (Space)') : t('video_editor_ui.play', 'Play (Space)')}
              title={isPlaying ? t('video_editor_ui.pause', 'Pause (Space)') : t('video_editor_ui.play', 'Play (Space)')}
            >
              {isPlaying ? <Pause size={22} /> : <Play size={22} />}
            </button>
            <button
              className="ve-icon-btn"
              onClick={() => stepFrame(1)}
              aria-label={t('video_editor_ui.next_frame', 'Next frame (→)')}
              title={t('video_editor_ui.next_frame', 'Next frame (→)')}
            >
              <SkipForward size={16} />
            </button>

            <span className="ve-time-display" aria-live="off">
              {fmtTime(currentTime)} / {fmtTime(duration)}
            </span>

            <div className="ve-trim-badges">
              <button
                className="ve-trim-point-btn"
                onClick={() => { setInPoint(currentTime); inPointRef.current = currentTime; }}
                title={t('video_editor_ui.set_in', 'Set In Point (I)')}
              >
                <Scissors size={11} aria-hidden />
                {t('video_editor_ui.in_label', 'In')}: {fmtTime(inPoint)}
              </button>
              <button
                className="ve-trim-point-btn"
                onClick={() => { setOutPoint(currentTime); outPointRef.current = currentTime; }}
                title={t('video_editor_ui.set_out', 'Set Out Point (O)')}
              >
                <Scissors size={11} aria-hidden />
                {t('video_editor_ui.out_label', 'Out')}: {fmtTime(outPoint)}
              </button>
              <button
                className="ve-trim-point-btn"
                onClick={splitAtPlayhead}
                title={t('video_editor_cut.split_hint', 'Split the video at the playhead (S)')}
              >
                <SplitSquareHorizontal size={11} aria-hidden />
                {t('video_editor_cut.split', 'Split')}
              </button>
              <button
                className="ve-trim-point-btn"
                onClick={togglePartAtPlayhead}
                disabled={!playheadRemoved && !cuts.splits.length}
                title={playheadRemoved
                  ? t('video_editor_cut.restore_hint', 'Put the part under the playhead back (Delete)')
                  : t('video_editor_cut.delete_hint', 'Remove the part under the playhead from the video (Delete)')}
              >
                {playheadRemoved ? <RotateCcw size={11} aria-hidden /> : <Trash2 size={11} aria-hidden />}
                {playheadRemoved ? t('video_editor_cut.restore', 'Restore part') : t('video_editor_cut.delete', 'Delete part')}
              </button>
              <span className="ve-duration-badge">
                {t('video_editor_ui.selected', '{{time}} selected', { time: fmtTime(keptLength) })}
              </span>
            </div>
          </div>

          {/* Scrubber with trim handles */}
          <div className="ve-scrubber-area">
            <div
              className="ve-scrubber"
              ref={scrubberRef}
              onMouseDown={e => handleScrubberMouseDown(e, 'playhead')}
              role="slider"
              aria-label={t('video_editor_ui.timeline_aria', 'Video timeline')}
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-valuenow={currentTime}
            >
              {/* Dimmed regions outside trim */}
              <div className="ve-scrubber-dim ve-scrubber-dim-left" style={{ width: `${inPct}%` }} />
              <div className="ve-scrubber-dim ve-scrubber-dim-right" style={{ width: `${100 - outPct}%` }} />

              {/* Active trim zone */}
              <div
                className="ve-scrubber-range"
                style={{ left: `${inPct}%`, width: `${outPct - inPct}%` }}
              />

              {/* Cuts: removed parts, split lines, the part under the playhead */}
              {cuts.removed.map(r => (
                        <div
                          key={`cut-${r.start}`}
                          className="ve-cut-removed"
                          style={{ left: `${pctOf(r.start)}%`, width: `${pctOf(r.end - r.start)}%` }}
                          title={t('video_editor_cut.removed', 'Removed part')}
                        />
                      ))}
                      {cuts.splits.map(sp => (
                        <div key={`split-${sp}`} className="ve-cut-split" style={{ left: `${pctOf(sp)}%` }} aria-hidden />
                      ))}
              {currentPart && (
                <div
                  className="ve-cut-current"
                  style={{ left: `${pctOf(currentPart.start)}%`, width: `${pctOf(currentPart.end - currentPart.start)}%` }}
                  aria-hidden
                />
              )}

              {/* In handle */}
              <div
                className="ve-trim-handle ve-trim-in"
                style={{ left: `${inPct}%` }}
                onMouseDown={e => { e.stopPropagation(); handleScrubberMouseDown(e, 'in'); }}
                title={`In: ${fmtTime(inPoint)}`}
                role="slider"
                aria-label={t('video_editor_ui.in_point', 'In point')}
                aria-valuenow={inPoint}
                tabIndex={0}
              />

              {/* Out handle */}
              <div
                className="ve-trim-handle ve-trim-out"
                style={{ left: `${outPct}%` }}
                onMouseDown={e => { e.stopPropagation(); handleScrubberMouseDown(e, 'out'); }}
                title={`Out: ${fmtTime(outPoint)}`}
                role="slider"
                aria-label={t('video_editor_ui.out_point', 'Out point')}
                aria-valuenow={outPoint}
                tabIndex={0}
              />

              {/* Playhead */}
              <div className="ve-playhead" style={{ left: `${playPct}%` }} aria-hidden />
            </div>

            {/* Time ticks */}
            <div className="ve-time-ticks" aria-hidden>
              {Array.from({ length: 9 }).map((_, i) => (
                <span key={i} style={{ position: 'absolute', left: `${i * 12.5}%` }}>
                  {fmtTime((i / 8) * duration)}
                </span>
              ))}
            </div>
          </div>

          {/* ── Timeline (advanced mode) ── */}
          {mode === 'advanced' && (
            <div className="ve-timeline">
              <div className="ve-timeline-header">
                <span className="ve-timeline-label">{t('video_editor_ui.timeline', 'Timeline')}</span>
                <button
                  type="button"
                  className="ve-timeline-add"
                  onClick={addZoomAtPlayhead}
                  title={t('video_editor_zoom.add', 'Add zoom at playhead (Z)')}
                >
                  <Plus size={13} aria-hidden />
                  {t('video_editor_zoom.add_short', 'Add zoom')}
                  <kbd>Z</kbd>
                </button>
                <div className="ve-timeline-zoom">
                  <button
                    className="ve-icon-btn small"
                    onClick={() => setTimelineZoom(z => Math.max(0.5, +(z - 0.5).toFixed(1)))}
                    title={t('video_editor_zoom.timeline_scale_out', 'Shrink the timeline view (does not change the video)')}
                    aria-label={t('video_editor_ui.zoom_out', 'Zoom out timeline')}
                    disabled={timelineZoom <= 0.5}
                  >
                    <ZoomOut size={13} />
                  </button>
                  <span className="ve-zoom-value">{timelineZoom}×</span>
                  <button
                    className="ve-icon-btn small"
                    onClick={() => setTimelineZoom(z => Math.min(4, +(z + 0.5).toFixed(1)))}
                    title={t('video_editor_zoom.timeline_scale_in', 'Stretch the timeline view (does not change the video)')}
                    aria-label={t('video_editor_ui.zoom_in', 'Zoom in timeline')}
                    disabled={timelineZoom >= 4}
                  >
                    <ZoomIn size={13} />
                  </button>
                </div>
              </div>

              <div className="ve-timeline-scroll">
                <div className="ve-timeline-track-area" style={{ width: `${timelineZoom * 100}%` }}>
                  {/* Video track row */}
                  <div className="ve-timeline-row">
                    <div className="ve-timeline-row-label">
                      <Monitor size={12} aria-hidden />
                      <span>{t('video_editor_ui.video_track', 'Video')}</span>
                    </div>
                    <div className="ve-timeline-track" ref={timelineTrackRef}>
                      {/* Inactive regions */}
                      <div className="ve-tl-inactive" style={{ width: `${inPct}%` }} />
                      <div className="ve-tl-inactive ve-tl-inactive-right" style={{ left: `${outPct}%`, width: `${100 - outPct}%` }} />

                      {/* Active clip block */}
                      <div
                        className="ve-timeline-clip"
                        style={{ left: `${inPct}%`, width: `${outPct - inPct}%` }}
                      >
                        <span className="ve-clip-label">{video.title}</span>
                      </div>

                      {cuts.removed.map(r => (
                        <div
                          key={`cut-${r.start}`}
                          className="ve-cut-removed"
                          style={{ left: `${pctOf(r.start)}%`, width: `${pctOf(r.end - r.start)}%` }}
                          title={t('video_editor_cut.removed', 'Removed part')}
                        />
                      ))}
                      {cuts.splits.map(sp => (
                        <div key={`split-${sp}`} className="ve-cut-split" style={{ left: `${pctOf(sp)}%` }} aria-hidden />
                      ))}

                      {/* Visual overlay markers (text, shapes, image, emoji — not audio) */}
                      {visualTimelineAnnotations.map(ann => {
                        const markerLeft = isFinite(duration) && duration > 0
                          ? (ann.inTime / duration) * 100 : 0;
                        const markerWidth = isFinite(duration) && duration > 0
                          ? Math.max(1.5, ((ann.outTime - ann.inTime) / duration) * 100) : 1.5;
                        const isResizing = resizingAnn?.id === ann.id;
                        return (
                          <div
                            key={ann.id}
                            className={`ve-ann-marker${selectedAnn === ann.id ? ' selected' : ''}${isResizing ? ' resizing' : ''}`}
                            style={{ left: `${markerLeft}%`, width: `${markerWidth}%` }}
                            aria-label={`${ann.type} annotation: ${ann.text || fmtTime(ann.inTime) + ' → ' + fmtTime(ann.outTime)}`}
                          >
                            <div
                              className="ve-ann-mhandle ve-ann-mhandle-left"
                              onMouseDown={e => handleAnnResizeStart(e, ann, 'left', 'video')}
                              title={`In: ${fmtTime(ann.inTime)} — drag to adjust`}
                            />

                            <div
                              className="ve-ann-mhandle-center"
                              onMouseDown={e => handleAnnResizeStart(e, ann, 'center', 'video')}
                              onClick={() => setSelectedAnn(ann.id)}
                              title={`${ann.text || ann.type}  ${fmtTime(ann.inTime)} → ${fmtTime(ann.outTime)}`}
                            >
                              <span className="ve-ann-marker-label">
                                {annotationTimelineLabel(ann)}
                              </span>
                            </div>

                            <div
                              className="ve-ann-mhandle ve-ann-mhandle-right"
                              onMouseDown={e => handleAnnResizeStart(e, ann, 'right', 'video')}
                              title={`Out: ${fmtTime(ann.outTime)} — drag to adjust`}
                            />
                          </div>
                        );
                      })}

                      <div
                        className="ve-timeline-playhead"
                        style={{ left: `${playPct}%` }}
                        aria-hidden
                      />
                    </div>
                  </div>

                  {/* Zoom track row */}
                  <div className="ve-timeline-row">
                    <div className="ve-timeline-row-label">
                      <ZoomIn size={12} aria-hidden />
                      <span>{t('video_editor_zoom.title', 'Zoom')}</span>
                    </div>
                    <div className="ve-timeline-track ve-timeline-track--zoom" ref={timelineZoomTrackRef}>
                      {zooms.length === 0 && (
                        <button type="button" className="ve-zoom-empty" onClick={addZoomAtPlayhead}>
                          <Plus size={12} aria-hidden />
                          {t('video_editor_zoom.empty', 'Add a zoom at the playhead (or press Z)')}
                        </button>
                      )}
                      {zooms.map(z => {
                        const left = isFinite(duration) && duration > 0 ? (z.start / duration) * 100 : 0;
                        const width = isFinite(duration) && duration > 0
                          ? Math.max(1.5, ((z.end - z.start) / duration) * 100) : 1.5;
                        const span = { id: z.id, inTime: z.start, outTime: z.end };
                        return (
                          <div
                            key={z.id}
                            className={`ve-ann-marker ve-ann-marker--zoom${selectedZoom === z.id ? ' selected' : ''}${resizingAnn?.id === z.id ? ' resizing' : ''}`}
                            style={{ left: `${left}%`, width: `${width}%` }}
                          >
                            <div className="ve-ann-mhandle ve-ann-mhandle-left" onMouseDown={e => handleAnnResizeStart(e, span, 'left', 'zoom')} />
                            <div
                              className="ve-ann-mhandle-center"
                              onMouseDown={e => handleAnnResizeStart(e, span, 'center', 'zoom')}
                              title={`${fmtTime(z.start)} → ${fmtTime(z.end)}`}
                            >
                              <span className="ve-ann-marker-label">{Math.round(z.scale * 100)}%</span>
                            </div>
                            <div className="ve-ann-mhandle ve-ann-mhandle-right" onMouseDown={e => handleAnnResizeStart(e, span, 'right', 'zoom')} />
                          </div>
                        );
                      })}
                      <div className="ve-timeline-playhead" style={{ left: `${playPct}%` }} aria-hidden />
                    </div>
                  </div>

                  <div className="ve-timeline-row ve-timeline-row--audio">
                    <div className="ve-timeline-row-label">
                      <button
                        className={`ve-mute-btn${muteOriginalAudio ? ' muted' : ''}`}
                        onClick={() => setMuteOriginalAudio(m => !m)}
                        title={muteOriginalAudio
                          ? t('video_editor_tools.unmute_original_audio', 'Unmute original audio')
                          : t('video_editor_tools.mute_original_audio', 'Mute original audio')}
                        aria-label={muteOriginalAudio
                          ? t('video_editor_tools.unmute_original_audio', 'Unmute original audio')
                          : t('video_editor_tools.mute_original_audio', 'Mute original audio')}
                      >
                        {muteOriginalAudio ? <VolumeX size={12} /> : <Mic size={12} />}
                      </button>
                      <span>{t('video_editor_props.timeline_audio_track')}</span>
                    </div>
                    <div className="ve-timeline-track ve-timeline-track--audio" ref={timelineAudioTrackRef}>
                      <div className="ve-tl-inactive" style={{ width: `${inPct}%` }} />
                      <div className="ve-tl-inactive ve-tl-inactive-right" style={{ left: `${outPct}%`, width: `${100 - outPct}%` }} />
                      <div
                        className="ve-timeline-audio-lane"
                        style={{ left: `${inPct}%`, width: `${outPct - inPct}%` }}
                        aria-hidden
                      />

                      {audioTimelineAnnotations.map(ann => {
                        const markerLeft = isFinite(duration) && duration > 0
                          ? (ann.inTime / duration) * 100 : 0;
                        const markerWidth = isFinite(duration) && duration > 0
                          ? Math.max(1.5, ((ann.outTime - ann.inTime) / duration) * 100) : 1.5;
                        const isResizing = resizingAnn?.id === ann.id;
                        const vol = Math.round((ann.audioVolume ?? 1) * 100);
                        return (
                          <div
                            key={ann.id}
                            className={`ve-ann-marker ve-ann-marker--audio${selectedAnn === ann.id ? ' selected' : ''}${isResizing ? ' resizing' : ''}`}
                            style={{ left: `${markerLeft}%`, width: `${markerWidth}%` }}
                            aria-label={`${ann.type}: ${ann.text || fmtTime(ann.inTime) + ' → ' + fmtTime(ann.outTime)}`}
                          >
                            <div
                              className="ve-ann-mhandle ve-ann-mhandle-left"
                              onMouseDown={e => handleAnnResizeStart(e, ann, 'left', 'audio')}
                              title={`In: ${fmtTime(ann.inTime)} — drag to adjust`}
                            />
                            <div
                              className="ve-ann-mhandle-center"
                              onMouseDown={e => handleAnnResizeStart(e, ann, 'center', 'audio')}
                              onClick={() => setSelectedAnn(ann.id)}
                              title={`${vol}% vol · ${fmtTime(ann.inTime)} → ${fmtTime(ann.outTime)}`}
                            >
                              <span className="ve-ann-marker-label">
                                {annotationTimelineLabel(ann)}
                                <span className="ve-ann-marker-vol"> {vol}%</span>
                              </span>
                            </div>
                            <button
                              className="ve-ann-delete-btn"
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); deleteAnnotation(ann.id); }}
                              title={t('video_editor_tools.delete_audio_track', 'Delete audio clip')}
                              aria-label={t('video_editor_tools.delete_audio_track', 'Delete audio clip')}
                            >
                              <Trash2 size={10} />
                            </button>
                            <div
                              className="ve-ann-mhandle ve-ann-mhandle-right"
                              onMouseDown={e => handleAnnResizeStart(e, ann, 'right', 'audio')}
                              title={`Out: ${fmtTime(ann.outTime)} — drag to adjust`}
                            />
                          </div>
                        );
                      })}

                      <div
                        className="ve-timeline-playhead"
                        style={{ left: `${playPct}%` }}
                        aria-hidden
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Annotations list */}
              {annotations.length > 0 && (
                <div className="ve-ann-list">
                  <h3 className="ve-ann-list-title">Annotations ({annotations.length})</h3>
                  <div className="ve-ann-list-items">
                    {annotations.map(ann => (
                      <div
                        key={ann.id}
                        className={`ve-ann-item${selectedAnn === ann.id ? ' selected' : ''}`}
                        onClick={() => { setSelectedAnn(ann.id); seekTo(ann.inTime); }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && setSelectedAnn(ann.id)}
                      >
                        <span className="ve-ann-badge">{ann.type}</span>
                        <span className="ve-ann-item-text">
                          {annotationTimelineLabel(ann)}
                        </span>
                        <span className="ve-ann-time">{fmtTime(ann.inTime)}</span>
                        <button
                          className="ve-icon-btn small"
                          onClick={e => { e.stopPropagation(); deleteAnnotation(ann.id); }}
                          aria-label={`Delete ${ann.text || ann.type} annotation`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* ── Save As modal ── */}
      {showSaveModal && (
        <div
          className="ve-modal-overlay"
          role="dialog"
          aria-modal
          aria-labelledby="ve-save-title"
          onClick={e => { if (e.target === e.currentTarget && !isCapturing && !isUploading) setShowSaveModal(false); }}
        >
          <div className="ve-modal">
            <h2 id="ve-save-title" className="ve-modal-title">
              <Save size={16} aria-hidden />
              {t('video_editor_save.title')}
            </h2>

            <div className="ve-modal-summary">
              <div className="ve-modal-row">
                <span>{t('video_editor_save.duration')}</span>
                <strong>{fmtTime(keptLength)}</strong>
              </div>
              {removedCount > 0 && (
                <div className="ve-modal-row">
                  <span>{t('video_editor_cut.removed_parts', 'Removed parts')}</span>
                  <strong>{removedCount}</strong>
                </div>
              )}
              {mode === 'advanced' && (
                <div className="ve-modal-row">
                  <span>{t('video_editor_save.annotations')}</span>
                  <strong>{annotations.length}</strong>
                </div>
              )}
              <p className="ve-modal-note">
                {t('video_editor_save.trim_note')}
              </p>
              <p className="ve-modal-note" style={{ borderTop: 'none', paddingTop: 0 }}>
                {t('video_editor_save.draft_note')}
              </p>
            </div>

            <label className="ve-modal-label" htmlFor="ve-filename">
              {t('video_editor_save.file_name_label')}
              <input
                id="ve-filename"
                className="ve-modal-input"
                value={saveFileName}
                onChange={e => setSaveFileName(e.target.value)}
                disabled={isCapturing || isUploading}
              />
            </label>

            {isUploading ? (
              <div className="ve-capture-progress" role="status">
                <span className="ve-capture-status">
                  <span className="ve-spinner" aria-hidden />
                  {t('video_editor_save.uploading', 'Uploading to My Library… keep this window open.')}
                </span>
                <div className="ve-progress-bar ve-progress-bar--busy" aria-hidden>
                  <div className="ve-progress-fill" />
                </div>
              </div>
            ) : isCapturing ? (
              <div className="ve-capture-progress">
                <span className="ve-capture-status">
                  <span className="ve-spinner" aria-hidden />
                  {captureKind === 'library'
                    ? t('video_editor_save.capture_step_render_upload', 'Step 1 of 2 — rendering the video')
                    : t('video_editor_save.capture_progress_download')}
                  <strong className="ve-capture-pct">{Math.round(captureProgress * 100)}%</strong>
                </span>
                <div className="ve-progress-bar" role="progressbar" aria-valuenow={Math.round(captureProgress * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <div className="ve-progress-fill" style={{ width: `${captureProgress * 100}%` }} />
                </div>
                {!fastRender && (
                  <span>{t('video_editor_save.capture_realtime_note', 'The video plays through once in real time while it renders.')}</span>
                )}
                <div className="ve-modal-actions">
                  <button type="button" className="ve-btn-secondary" onClick={cancelCapture}>
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="ve-modal-actions-wrap">
                <div className="ve-modal-actions-row ve-modal-actions-row--primary">
                  <button
                    type="button"
                    className="ve-btn-secondary"
                    onClick={() => void handleSaveDraft()}
                  >
                    <BookmarkPlus size={14} aria-hidden />
                    {t('video_editor_save.save_draft')}
                  </button>
                  <button
                    type="button"
                    className="ve-btn-primary ve-btn-primary--outline"
                    onClick={() => void startCapture('download')}
                  >
                    <Download size={14} aria-hidden />
                    {t('video_editor_save.render_download')}
                  </button>
                  <button
                    type="button"
                    className="ve-btn-primary"
                    onClick={() => void startCapture('library')}
                  >
                    <UploadCloud size={14} aria-hidden />
                    {t('video_editor_save.upload_library')}
                  </button>
                </div>
                <div className="ve-modal-actions">
                  <button
                    type="button"
                    className="ve-btn-secondary"
                    onClick={() => setShowSaveModal(false)}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {voiceCapturePhase !== 'idle' && (
        <div className="ve-voice-capture-overlay">
          <div
            className="ve-voice-capture-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ve-voice-capture-title"
          >
            <h2 id="ve-voice-capture-title" className="ve-voice-capture-title">
              {voiceCapturePhase === 'review'
                ? t('video_editor_tools.voice_review_title')
                : voiceCapturePhase === 'paused'
                  ? t('video_editor_tools.voice_paused_title')
                  : t('video_editor_tools.voice_recording_title')}
            </h2>

            {(voiceCapturePhase === 'recording' || voiceCapturePhase === 'paused') && (
              <p className="ve-voice-capture-timer" aria-live="polite">
                {formatVoiceElapsed(getVoiceElapsedSec())}
              </p>
            )}

            {voiceCapturePhase === 'review' && voiceReviewUrl ? (
              <audio
                key={voiceReviewUrl}
                className="ve-voice-capture-audio"
                controls
                src={voiceReviewUrl}
                aria-label={t('video_editor_tools.voice_review_audio_label')}
              >
                {t('video_editor_tools.voice_review_audio_fallback')}
              </audio>
            ) : null}

            <div className="ve-voice-capture-actions">
              {voiceCapturePhase === 'recording' && (
                <>
                  {canPauseMediaRecorder ? (
                    <button
                      type="button"
                      className="ve-btn-secondary"
                      onClick={() => pauseVoiceCapture()}
                      aria-label={t('video_editor_tools.pause_recording')}
                    >
                      <Pause size={16} aria-hidden />
                      {t('video_editor_tools.pause_recording')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="ve-btn-primary"
                    onClick={() => finalizeVoiceCaptureToReview()}
                    aria-label={t('video_editor_tools.finish_capture')}
                  >
                    <Square size={16} aria-hidden />
                    {t('video_editor_tools.finish_capture')}
                  </button>
                  <button
                    type="button"
                    className="ve-btn-secondary"
                    onClick={() => cancelVoiceCapture()}
                    aria-label={t('video_editor_tools.cancel_recording')}
                  >
                    <X size={16} aria-hidden />
                    {t('cancel')}
                  </button>
                </>
              )}
              {voiceCapturePhase === 'paused' && (
                <>
                  <button
                    type="button"
                    className="ve-btn-primary"
                    onClick={() => resumeVoiceCapture()}
                    aria-label={t('video_editor_tools.resume_recording')}
                  >
                    <Play size={16} aria-hidden />
                    {t('video_editor_tools.resume_recording')}
                  </button>
                  <button
                    type="button"
                    className="ve-btn-primary ve-btn-primary--outline"
                    onClick={() => finalizeVoiceCaptureToReview()}
                    aria-label={t('video_editor_tools.finish_capture')}
                  >
                    <Square size={16} aria-hidden />
                    {t('video_editor_tools.finish_capture')}
                  </button>
                  <button
                    type="button"
                    className="ve-btn-secondary"
                    onClick={() => cancelVoiceCapture()}
                    aria-label={t('video_editor_tools.cancel_recording')}
                  >
                    <X size={16} aria-hidden />
                    {t('cancel')}
                  </button>
                </>
              )}
              {voiceCapturePhase === 'review' && (
                <>
                  <button
                    type="button"
                    className="ve-btn-primary"
                    onClick={() => insertVoiceReviewToTimeline()}
                    aria-label={t('video_editor_tools.insert_voice_clip')}
                  >
                    <Check size={16} aria-hidden />
                    {t('video_editor_tools.insert_voice_clip')}
                  </button>
                  <button
                    type="button"
                    className="ve-btn-secondary"
                    onClick={() => discardReviewAndRecordAgain()}
                    aria-label={t('video_editor_tools.rerecord_voice')}
                  >
                    <RotateCcw size={16} aria-hidden />
                    {t('video_editor_tools.rerecord_voice')}
                  </button>
                  <button
                    type="button"
                    className="ve-btn-secondary"
                    onClick={() => cancelVoiceReview()}
                    aria-label={t('video_editor_tools.discard_voice_review')}
                  >
                    <X size={16} aria-hidden />
                    {t('cancel')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {storageExceeded && (
        <StorageExceededDialog
          storageInfo={storageInfo}
          onClose={() => setStorageExceeded(false)}
        />
      )}
    </div>
  );
}
